import { useState } from 'react';
import { X } from 'lucide-react';

interface DataDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  source: 'linkedin_360' | 'glassdoor' | 'consumer_intel' | 'crunchbase' | 'g2_reviews' | 'trustpilot' | 'instagram' | 'facebook' | 'youtube' | 'tiktok' | 'x' | 'website' | 'threads';
  data: any;
  scrapedAt?: string;
}

// Helper to safely escape HTML
const esc = (str: string | null | undefined): string => {
  if (!str) return '';
  return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
};

// Helper to safely get string value
const getStr = (val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    if (val.text) return val.text;
    if (val.value) return val.value;
    if (val.name) return val.name;
    return '';
  }
  return String(val);
};

// Helper to detect if text is English
const isEnglish = (text: string): boolean => {
  if (!text || typeof text !== 'string') return true;
  const nonEnglish = /[àâäéèêëïîôùûüçæœÀÂÄÉÈÊËÏÎÔÙÛÜÇ]/;
  if (nonEnglish.test(text)) return false;
  const asciiLetters = text.match(/[a-zA-Z]/g) || [];
  const allLetters = text.match(/\p{L}/gu) || [];
  return allLetters.length === 0 || asciiLetters.length / allLetters.length > 0.8;
};

// Helper to categorize job titles into departments
const getDepartment = (title: string): string => {
  if (!title) return 'Other';
  const t = title.toLowerCase();
  if (t.includes('engineer') || t.includes('developer') || t.includes('software') || t.includes('swe') || t.includes('devops') || t.includes('architect')) return 'Engineering';
  if (t.includes('data') || t.includes('analyst') || t.includes('scientist') || t.includes('analytics') || t.includes('bi ')) return 'Data & Analytics';
  if (t.includes('product') || t.includes('pm ') || t.includes('program manager')) return 'Product';
  if (t.includes('design') || t.includes('ux') || t.includes('ui') || t.includes('creative')) return 'Design';
  if (t.includes('sales') || t.includes('account exec') || t.includes('business develop') || t.includes('revenue')) return 'Sales';
  if (t.includes('marketing') || t.includes('brand') || t.includes('content') || t.includes('seo') || t.includes('growth')) return 'Marketing';
  if (t.includes('hr') || t.includes('recruit') || t.includes('people') || t.includes('talent') || t.includes('human resource')) return 'HR & Recruiting';
  if (t.includes('finance') || t.includes('account') || t.includes('financial') || t.includes('controller') || t.includes('treasury')) return 'Finance';
  if (t.includes('ops') || t.includes('operations') || t.includes('logistics') || t.includes('supply chain')) return 'Operations';
  if (t.includes('legal') || t.includes('counsel') || t.includes('compliance')) return 'Legal';
  if (t.includes('executive') || t.includes('chief') || t.includes('vp ') || t.includes('vice president') || t.includes('director') || t.includes('manager')) return 'Leadership';
  if (t.includes('intern') || t.includes('internship')) return 'Internships';
  if (t.includes('support') || t.includes('customer') || t.includes('service') || t.includes('success')) return 'Customer Success';
  return 'Other';
};

// Format pay as XXk with optional currency code
const formatPay = (amount: number | null | undefined, currencyCode?: string): string => {
  if (!amount) return '';
  const num = Math.round(amount / 1000);
  const prefix = currencyCode ? `${currencyCode} ` : '$';
  return `${prefix}${num}k`;
};

const summarizeWebsiteData = (data: unknown): {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  domains?: string[];
  phones?: string[];
  address?: string;
  socials?: Record<string, string[]>;
  keywords?: string[];
  sample?: string;
  pagesCount: number;
} => {
  const pages = Array.isArray(data) ? data : [];
  const first = pages[0] as any;
  const meta = first?.metadata || {};
  const jsonLd = Array.isArray(meta?.jsonLd) ? meta.jsonLd : [];
  const graph = jsonLd
    .flatMap((node: any) => (node && node['@graph'] ? node['@graph'] : node))
    .filter(Boolean);
  const org =
    graph.find(
      (n: any) => n && (n['@type'] === 'Organization' || n['@type'] === 'Corporation')
    ) || {};
  const canonicalUrl = meta?.canonicalUrl || org?.url || first?.url || '';
  const description = meta?.description || org?.description || '';
  const title = meta?.title || org?.name || '';
  const address = org?.address
    ? [
        org.address?.streetAddress,
        org.address?.addressLocality,
        org.address?.addressRegion,
        org.address?.postalCode,
        org.address?.addressCountry,
      ]
        .filter(Boolean)
        .join(', ')
    : '';
  const contactPoints = Array.isArray(org?.contactPoint) ? org.contactPoint : [];
  const phones = contactPoints
    .flatMap((cp: any) =>
      Array.isArray(cp?.telephone)
        ? cp.telephone
        : cp?.telephone
          ? [cp.telephone]
          : []
    )
    .filter(Boolean);
  const sameAs = Array.isArray(org?.sameAs) ? org.sameAs : [];
  const socials: Record<string, string[]> = {
    linkedin: [],
    facebook: [],
    instagram: [],
    tiktok: [],
    youtube: [],
    x: [],
    podcast: [],
  };
  const addSocial = (key: keyof typeof socials, value: unknown) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (typeof v === 'string' && v) socials[key].push(v);
      });
      return;
    }
    if (typeof value === 'string') socials[key].push(value);
  };
  for (const url of sameAs) {
    if (typeof url !== 'string') continue;
    if (url.includes('linkedin.com')) socials.linkedin.push(url);
    else if (url.includes('facebook.com')) socials.facebook.push(url);
    else if (url.includes('instagram.com')) socials.instagram.push(url);
    else if (url.includes('tiktok.com')) socials.tiktok.push(url);
    else if (url.includes('youtube.com') || url.includes('youtu.be')) socials.youtube.push(url);
    else if (url.includes('x.com') || url.includes('twitter.com')) socials.x.push(url);
    else if (
      url.includes('podcast') ||
      url.includes('podbean') ||
      url.includes('spotify') ||
      url.includes('apple.com')
    )
      socials.podcast.push(url);
  }
  for (const page of pages as any[]) {
    addSocial('linkedin', page?.linkedins);
    addSocial('facebook', page?.facebooks);
    addSocial('instagram', page?.instagrams);
    addSocial('tiktok', page?.tiktoks);
    addSocial('youtube', page?.youtubes);
    addSocial('x', page?.twitters);
    addSocial('x', page?.xs);
    addSocial('podcast', page?.podcasts);
  }
  const keywords = Array.isArray(meta?.keywords)
    ? meta.keywords
    : typeof meta?.keywords === 'string'
      ? meta.keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
      : [];
  const sample = typeof first?.text === 'string' ? first.text.slice(0, 420) : '';
  const domains = canonicalUrl ? [canonicalUrl] : [];
  return {
    title,
    description,
    canonicalUrl,
    domains,
    phones,
    address,
    socials,
    keywords,
    sample,
    pagesCount: pages.length,
  };
};

const sourceNames: Record<string, string> = {
  linkedin_360: 'LinkedIn 360',
  glassdoor: 'Glassdoor 360',
  consumer_intel: 'Consumer Intel',
  crunchbase: 'Crunchbase',
  g2_reviews: 'Software Reviews',
  trustpilot: 'Trustpilot',
  instagram: 'Instagram',
  facebook: 'Facebook',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  x: 'X',
  website: 'Website',
  threads: 'Threads'
};

export function DataDrawer({ isOpen, onClose, companyName, source, data, scrapedAt }: DataDrawerProps) {
  if (!isOpen || !data) return null;

  return (
    <div className="ss-drawer">
      <div className="ss-drawer-overlay" onClick={onClose} />
      <div className="ss-drawer-panel">
        <div className="ss-drawer-inner">
          <div className="ss-drawer-head">
            <div>
              <h3 className="ss-drawer-title">{companyName} - {sourceNames[source] || source}</h3>
              {scrapedAt && <p className="ss-drawer-sub">Scraped: {new Date(scrapedAt).toLocaleString()}</p>}
            </div>
            <button onClick={onClose} className="ss-drawer-close">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            {source === 'linkedin_360' && <LinkedIn360Content data={data} companyName={companyName} />}
            {source === 'glassdoor' && <Glassdoor360Content data={data} />}
            {source === 'consumer_intel' && <ConsumerIntelContent data={data} />}
            {source === 'g2_reviews' && <G2MultiContent data={data} />}
            {source === 'trustpilot' && <TrustpilotOnlyContent data={data} />}
            {source === 'instagram' && <InstagramContent data={data} />}
            {source === 'facebook' && <FacebookContent data={data} />}
            {source === 'youtube' && <YouTubeContent data={data} />}
            {source === 'tiktok' && <TikTokContent data={data} />}
            {source === 'x' && <XContent data={data} />}
            {source === 'threads' && <ThreadsContent data={data} />}
            {source === 'website' && <WebsiteContent data={data} companyName={companyName} />}
            {source === 'crunchbase' && <CrunchbaseContent data={data} companyName={companyName} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function WebsiteContent({ data, companyName }: { data: any; companyName: string }) {
  const summary = summarizeWebsiteData(data);
  const [showRawDrawer, setShowRawDrawer] = useState(false);
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-slate-900 to-gray-900 border border-gray-700 rounded-xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Website Overview</p>
            <h4 className="text-lg font-semibold text-white">{summary.title || companyName}</h4>
            <p className="text-sm text-gray-400 mt-1">{summary.description || 'No meta description found.'}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-600 text-white">Pages {summary.pagesCount}</span>
            {summary.canonicalUrl && (
              <a className="text-xs text-indigo-300 hover:text-indigo-200" href={summary.canonicalUrl} target="_blank" rel="noreferrer">
                {summary.canonicalUrl.replace(/^https?:\/\//, '')}
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs uppercase text-gray-500 mb-2">Contact</p>
          <p className="text-sm text-gray-300">{summary.address || 'Address not found'}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {(summary.phones || []).length > 0 ? (
              summary.phones!.slice(0, 3).map((phone) => (
                <span key={phone} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">{phone}</span>
              ))
            ) : (
              <span className="text-xs text-gray-500">No phone numbers found</span>
            )}
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs uppercase text-gray-500 mb-2">Keywords</p>
          <div className="flex flex-wrap gap-2">
            {(summary.keywords || []).length > 0 ? (
              summary.keywords!.slice(0, 8).map((kw) => (
                <span key={kw} className="px-2 py-1 rounded bg-gray-800 text-xs text-gray-300">{kw}</span>
              ))
            ) : (
              <span className="text-xs text-gray-500">No keywords found</span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-3">Social Links</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {Object.entries(summary.socials || {}).map(([key, links]) => (
            <div key={key} className="bg-gray-800 rounded-lg p-2">
              <p className="text-gray-400">{key.toUpperCase()}</p>
              <p className="text-gray-200">{links.length ? links.length : 0}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-2">Sample</p>
        <p className="text-sm text-gray-300 whitespace-pre-line">{summary.sample || 'No sample text available.'}</p>
      </div>

      <button
        onClick={() => setShowRawDrawer((v) => !v)}
        className="text-xs text-gray-400 hover:text-gray-200 underline"
      >
        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRawDrawer && (
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-gray-300">{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// LinkedIn 360 Content
export function LinkedIn360Content({ data, companyName }: { data: any; companyName: string }) {
  const profile = data.company?.[0] ?? {};
  const description = esc(profile.description || '');
  const specialties = Array.isArray(profile.specialties) ? profile.specialties.filter(Boolean) : [];
  const similarPages = Array.isArray(profile.similarPages) ? profile.similarPages.filter(Boolean) : [];
  const employees = Array.isArray(profile.employees) ? profile.employees.filter(Boolean) : [];
  const location = profile.location ? `${profile.location.city || ''}${profile.location.state ? `, ${profile.location.state}` : ''}${profile.location.country ? `, ${profile.location.country}` : ''}`.replace(/^, /, '') : '';
  return (
    <>
      {/* Company Profile */}
      {data.company?.[0] && (
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-start gap-4">
            {profile.logo && (
              <img src={profile.logo} alt="Company logo" className="w-14 h-14 rounded bg-gray-900 border border-gray-700 object-contain" />
            )}
            <div className="flex-1">
              <h4 className="font-semibold text-blue-400 mb-1">Company Profile</h4>
              <p className="text-white font-semibold">{esc(profile.name || companyName)}</p>
              {profile.tagline && <p className="text-sm text-gray-400">{esc(profile.tagline)}</p>}
              {profile.slogan && <p className="text-sm text-gray-400">{esc(profile.slogan)}</p>}
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-300">
            {location && <div><span className="text-gray-500">Location:</span> {esc(location)}</div>}
            {profile.headquarters && <div><span className="text-gray-500">HQ:</span> {esc(profile.headquarters)}</div>}
            {profile.website && (
              <div>
                <span className="text-gray-500">Website:</span>{' '}
                <a href={profile.website} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200 underline">
                  {esc(profile.website)}
                </a>
              </div>
            )}
            {profile.industry && <div><span className="text-gray-500">Industry:</span> {esc(profile.industry)}</div>}
            {profile.size && <div><span className="text-gray-500">Size:</span> {esc(profile.size)}</div>}
            {profile.employeeCount && <div><span className="text-gray-500">Employees:</span> {esc(profile.employeeCount)}</div>}
            {profile.type && <div><span className="text-gray-500">Type:</span> {esc(profile.type)}</div>}
            {profile.founded && <div><span className="text-gray-500">Founded:</span> {esc(profile.founded)}</div>}
          </div>
          {description && (
            <div className="mt-3">
              <p className="text-sm text-gray-400 whitespace-pre-line">{description}</p>
            </div>
          )}
          {specialties.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-gray-500 mb-2">Specialties</div>
              <div className="flex flex-wrap gap-2">
                {specialties.map((s: string, idx: number) => (
                  <span key={idx} className="px-2 py-1 rounded bg-gray-900 border border-gray-700 text-xs text-gray-300">
                    {esc(s)}
                  </span>
                ))}
              </div>
            </div>
          )}
          {similarPages.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-gray-500 mb-2">Similar Pages</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {similarPages.slice(0, 10).map((p: any, idx: number) => (
                  <a key={idx} href={p.link} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200 underline text-sm">
                    {esc(p.name || p.link)}
                  </a>
                ))}
              </div>
            </div>
          )}
          {employees.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-gray-500 mb-2">Employees</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {employees.slice(0, 8).map((e: any, idx: number) => (
                  <a key={idx} href={e.link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200">
                    {e.image && <img src={e.image} className="w-6 h-6 rounded-full object-cover" alt={esc(e.name)} />}
                    <span>{esc(e.name || e.link)}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Posts */}
      {data.posts?.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-blue-400 mb-3">Recent Posts ({data.posts.length})</h4>
          {data.posts.slice(0, 5).map((p: any, i: number) => {
            const postText = p.text || p.postText || p.content || p.commentary || '';
            const postDate = p.postedDate || p.publishedAt || p.date || '';
            const likes = p.numLikes || p.likesCount || p.socialCounts?.numLikes || 0;
            const comments = p.numComments || p.commentsCount || p.socialCounts?.numComments || 0;
            return (
              <div key={i} className="border-b border-gray-700 py-3 last:border-0">
                <p className="text-sm text-gray-200 leading-relaxed">{esc(postText.substring(0, 300))}{postText.length > 300 ? '...' : ''}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  {postDate && <span>📅 {esc(postDate)}</span>}
                  <span>👍 {likes} likes</span>
                  <span>💬 {comments} comments</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Open Jobs by Department */}
      {data.jobs?.length > 0 && <LinkedInJobsSection jobs={data.jobs} />}

      {/* Raw fallback (connector outputs vary a lot) */}
      <details className="border border-gray-700 rounded bg-gray-800">
        <summary className="p-3 cursor-pointer text-sm text-gray-300 hover:bg-gray-700">
          View raw LinkedIn 360 data
        </summary>
        <div className="border-t border-gray-700 p-3 bg-gray-900">
          <pre className="text-xs text-gray-400 overflow-auto max-h-96">{JSON.stringify(data, null, 2)}</pre>
        </div>
      </details>
    </>
  );
}

function LinkedInJobsSection({ jobs }: { jobs: any[] }) {
  const departments: Record<string, any[]> = {};
  jobs.forEach(j => {
    const title = j.title || j.jobTitle || '';
    const dept = getDepartment(title);
    if (!departments[dept]) departments[dept] = [];
    departments[dept].push(j);
  });

  const sortedDepts = Object.entries(departments).sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h4 className="font-semibold text-blue-400 mb-3">Open Positions ({jobs.length} total)</h4>
      <div className="space-y-2">
        {sortedDepts.map(([dept, deptJobs], idx) => {
          const locations = [...new Set(deptJobs.map(j => j.location || j.city || '').filter(Boolean))];
          const locationStr = locations.slice(0, 3).join(', ') + (locations.length > 3 ? ` +${locations.length - 3} more` : '');
          
          return (
            <details key={idx} className="border border-gray-700 rounded">
              <summary className="flex items-center justify-between p-3 hover:bg-gray-700 cursor-pointer">
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">{dept}</span>
                  <span className="text-xs text-gray-500">{deptJobs.length} roles</span>
                </div>
                {locationStr && <span className="text-xs text-yellow-400">{locationStr}</span>}
              </summary>
              <div className="border-t border-gray-700 p-3 bg-gray-900 space-y-2">
                {deptJobs.slice(0, 10).map((j, jIdx) => (
                  <div
                    key={jIdx}
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm border-b border-gray-800 pb-2 last:border-0"
                  >
                    <div>
                      <div className="text-blue-400">{esc(j.title || j.jobTitle || '')}</div>
                      <div className="text-xs">
                        <span className="text-yellow-400">{esc(j.location || j.city || '')}</span>
                        {getSalaryRange(j) ? (
                          <span className="text-green-400">{` · ${getSalaryRange(j)}`}</span>
                        ) : null}
                      </div>
                      {getPoster(j) && (
                        <div className="text-[11px] text-gray-400 mt-0.5">
                          Posted by{' '}
                          <a
                            href={getPoster(j)!.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-300 hover:text-blue-200 underline"
                          >
                            {getPoster(j)!.name}
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      {getJobAge(j) && (
                        <span className="px-2 py-1 rounded bg-gray-800 border border-gray-700">
                          {getJobAge(j)}
                        </span>
                      )}
                      {getSalaryRange(j) && (
                        <span className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-green-400">
                          {getSalaryRange(j)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}

function getJobAge(job: any) {
  const raw =
    job.postedAt ||
    job.postedDate ||
    job.publishedAt ||
    job.datePosted ||
    job.listedAt ||
    job.postedTime ||
    job.postedAgoShort ||
    job.postedAgoText;
  if (!raw) return '';
  if (typeof raw === 'string' && raw.toLowerCase().includes('ago')) return raw;
  const date = typeof raw === 'number' ? new Date(raw) : new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return '';
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function getSalaryRange(job: any) {
  const val =
    job.salaryRange ||
    job.salary ||
    job.compensation ||
    job.salaryText ||
    job.salaryRangeText;
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    const min = val.min || val.minimum || val.low;
    const max = val.max || val.maximum || val.high;
    const currency = val.currency || val.currencyCode || '';
    if (min && max) return `${currency ? currency + ' ' : ''}${min} - ${max}`;
    if (min) return `${currency ? currency + ' ' : ''}${min}+`;
    if (max) return `${currency ? currency + ' ' : ''}Up to ${max}`;
  }
  return '';
}

function getPoster(job: any) {
  const name = job.posterFullName || job.posterName || job.postedByName;
  const url = job.posterProfileUrl || job.posterUrl || job.postedByUrl;
  if (!name || !url) return null;
  return { name: String(name), url: String(url) };
}

// Glassdoor 360 Content
export function Glassdoor360Content({ data }: { data: any }) {
  return (
    <>
      {/* Company Overview */}
      {data.overview?.[0] && (
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-green-400">Company Overview</h4>
            {data.overview[0].rating && <span className="text-2xl font-bold text-yellow-400">⭐ {data.overview[0].rating}</span>}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
            {data.overview[0].reviewCount && <p>{data.overview[0].reviewCount} reviews</p>}
            {data.overview[0].industry && <p>Industry: {esc(data.overview[0].industry)}</p>}
            {data.overview[0].size && <p>Size: {esc(data.overview[0].size)}</p>}
            {data.overview[0].headquarters && <p>HQ: {esc(data.overview[0].headquarters)}</p>}
            {data.overview[0].revenue && <p>Revenue: {esc(data.overview[0].revenue)}</p>}
            {data.overview[0].ceoApproval && <p>CEO Approval: {data.overview[0].ceoApproval}%</p>}
            {data.overview[0].recommendToFriend && <p>Recommend: {data.overview[0].recommendToFriend}%</p>}
          </div>
        </div>
      )}

      {/* US Salaries by Department */}
      {data.salaries?.length > 0 && <GlassdoorSalariesSection salaries={data.salaries} />}

      {/* Interviews by Department */}
      {data.interviews?.length > 0 && <GlassdoorInterviewsSection interviews={data.interviews} />}

      {/* Reviews by Department */}
      {data.reviews?.length > 0 && <GlassdoorReviewsSection reviews={data.reviews} />}

      {/* Raw fallback */}
      <details className="border border-gray-700 rounded bg-gray-800">
        <summary className="p-3 cursor-pointer text-sm text-gray-300 hover:bg-gray-700">
          View raw Glassdoor 360 data
        </summary>
        <div className="border-t border-gray-700 p-3 bg-gray-900">
          <pre className="text-xs text-gray-400 overflow-auto max-h-96">{JSON.stringify(data, null, 2)}</pre>
        </div>
      </details>
    </>
  );
}

function GlassdoorSalariesSection({ salaries }: { salaries: any[] }) {
  if (!salaries || salaries.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="font-semibold text-green-400 mb-2">💰 Salary Data</h4>
        <p className="text-sm text-gray-400">No salary data available.</p>
      </div>
    );
  }

  const parseNumbers = (val: any): number[] => {
    if (val === null || val === undefined) return [];
    if (typeof val === 'number') return [val];
    if (typeof val === 'string') {
      const matches = val.match(/[\d,.]+/g) || [];
      return matches
        .map((m) => parseFloat(m.replace(/,/g, '')))
        .filter((n) => !isNaN(n));
    }
    if (typeof val === 'object') {
      if ('amount' in val) return parseNumbers((val as any).amount);
      if ('value' in val) return parseNumbers((val as any).value);
      if ('min' in val || 'max' in val || 'median' in val) {
        return [
          ...(parseNumbers((val as any).min)),
          ...(parseNumbers((val as any).median)),
          ...(parseNumbers((val as any).max)),
        ];
      }
    }
    return [];
  };

  const getPercentile = (percentiles: any[], ident: string) => {
    if (!percentiles) return null;
    const p = percentiles.find((x: any) => x.ident === ident);
    return p ? p.value : null;
  };

  const medianOf = (nums: number[]): number | null => {
    if (!nums.length) return null;
    const sorted = [...nums].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  };

  const salaryByDept: Record<string, any[]> = {};
  salaries.forEach(s => {
    const title = s.jobTitle?.text || getStr(s.jobTitle) || 'Unknown Role';
    const dept = getDepartment(title);
    if (!salaryByDept[dept]) salaryByDept[dept] = [];

    // Prefer reviewed salary data over ranges
    const reviewedAmounts: number[] = [];
    reviewedAmounts.push(...parseNumbers(s.salary));
    reviewedAmounts.push(...parseNumbers(s.salaryValue));
    reviewedAmounts.push(...parseNumbers(s.annualSalary));
    reviewedAmounts.push(...parseNumbers(s.pay));
    reviewedAmounts.push(...parseNumbers(s.payRate));
    reviewedAmounts.push(...parseNumbers(s.payAmount));
    reviewedAmounts.push(...parseNumbers(s.basePay));
    reviewedAmounts.push(...parseNumbers(s.totalPay));
    reviewedAmounts.push(...parseNumbers(s.estimatedSalary));
    reviewedAmounts.push(...parseNumbers(s.salaryMin));
    reviewedAmounts.push(...parseNumbers(s.salaryMax));

    const reviewLists =
      s.salary_list ||
      s.salaryList ||
      s.reviews ||
      s.review_list ||
      [];
    if (Array.isArray(reviewLists)) {
      reviewLists.forEach((r: any) => {
        reviewedAmounts.push(...parseNumbers(r.salary));
        reviewedAmounts.push(...parseNumbers(r.basePay));
        reviewedAmounts.push(...parseNumbers(r.totalPay));
        reviewedAmounts.push(...parseNumbers(r.amount));
      });
    }

    const reviewedMin = reviewedAmounts.length ? Math.min(...reviewedAmounts) : null;
    const reviewedMax = reviewedAmounts.length ? Math.max(...reviewedAmounts) : null;
    const reviewedMedian = reviewedAmounts.length ? medianOf(reviewedAmounts) : null;
    const reviewedCount = reviewedAmounts.length ? reviewedAmounts.length : 0;
    const reviewedCurrency =
      s.currency?.code ||
      s.currencyCode ||
      s.payCurrency ||
      s.basePay?.currency?.code ||
      s.totalPay?.currency?.code ||
      '';
    const payPeriod = s.payPeriod || s.pay_period || s.payPeriodType || s.payType || '';

    if (reviewedAmounts.length) {
      salaryByDept[dept].push({
        title,
        min: reviewedMin,
        median: reviewedMedian,
        max: reviewedMax,
        count: s.salaryCount || reviewedCount,
        currency: reviewedCurrency,
        payPeriod,
        source: 'reviewed'
      });
      return;
    }

    // Fallback to range statistics if reviewed data isn't present
    const stats = s.totalPayStatistics || s.payStatistics || s;
    salaryByDept[dept].push({
      title,
      min: getPercentile(stats?.percentiles, 'P25') || s.payPercentile25 || s.basePay?.min,
      median: getPercentile(stats?.percentiles, 'P50') || s.payPercentile50 || s.medianPay || s.basePay?.median,
      max: getPercentile(stats?.percentiles, 'P75') || s.payPercentile75 || s.basePay?.max,
      count: s.salaryCount || 0,
      currency: reviewedCurrency || s.currency?.code || '',
      payPeriod,
      source: 'range'
    });
  });

  const deptSummaries = Object.entries(salaryByDept).map(([dept, roles]) => {
    const allMedians = roles.map(r => r.median).filter(m => m && m > 0);
    const currencySet = new Set(roles.map(r => r.currency).filter(Boolean));
    const currencyCode = currencySet.size === 1 ? Array.from(currencySet)[0] : '';
    const minPay = allMedians.length ? Math.min(...allMedians) : 0;
    const maxPay = allMedians.length ? Math.max(...allMedians) : 0;
    return { dept, roles, minPay, maxPay, currencyCode, multipleCurrencies: currencySet.size > 1 };
  }).sort((a, b) => b.roles.length - a.roles.length);

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h4 className="font-semibold text-green-400">💰 Salary Data ({salaries.length} roles)</h4>
        <span className="text-xs text-gray-400">
          Reviewed pay shown when available. Otherwise: modeled range.
        </span>
      </div>
      <div className="space-y-2">
        {deptSummaries.map((d, idx) => (
          <details key={idx} className="border border-gray-700 rounded">
            <summary className="flex items-center justify-between p-3 hover:bg-gray-700 cursor-pointer">
              <div className="flex items-center gap-3">
                <span className="text-white font-medium">{d.dept}</span>
                <span className="text-xs text-gray-500">{d.roles.length} roles</span>
              </div>
              {d.multipleCurrencies && (
                <span className="text-xs text-gray-400">Multiple currencies</span>
              )}
              {!d.multipleCurrencies && d.minPay && d.maxPay && (
                <span className="text-green-400 text-sm font-medium">
                  {formatPay(d.minPay, d.currencyCode)} - {formatPay(d.maxPay, d.currencyCode)}
                </span>
              )}
            </summary>
            <div className="border-t border-gray-700 bg-gray-900">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 border-b border-gray-800">
                  <tr>
                    <th className="text-left p-2">Role</th>
                    <th className="text-right p-2">Min</th>
                    <th className="text-right p-2">Median</th>
                    <th className="text-right p-2">Max</th>
                    <th className="text-right p-2">#</th>
                    <th className="text-right p-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {d.roles.map((r, rIdx) => (
                    <tr key={rIdx} className="border-b border-gray-800 last:border-0 hover:bg-gray-800">
                      <td className="p-2 text-gray-300">
                        {esc(r.title)}
                        {r.payPeriod && (
                          <div className="text-xs text-gray-500">{esc(r.payPeriod)}</div>
                        )}
                      </td>
                      <td className="p-2 text-right text-gray-400">{formatPay(r.min, r.currency)}</td>
                      <td className="p-2 text-right text-green-400 font-medium">{formatPay(r.median, r.currency)}</td>
                      <td className="p-2 text-right text-gray-400">{formatPay(r.max, r.currency)}</td>
                      <td className="p-2 text-right text-gray-500">{r.count}</td>
                      <td className="p-2 text-right text-xs text-gray-500">
                        {r.source === 'reviewed'
                          ? 'Reviewed salary'
                          : 'Modeled range (local comps + limited reports)'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function GlassdoorInterviewsSection({ interviews }: { interviews: any[] }) {
  const englishInterviews = interviews.filter(i => {
    const text = getStr(i.interviewQuestions || i.questions || i.question || i.process || '');
    return isEnglish(text);
  });

  if (englishInterviews.length === 0) return null;

  const interviewsByDept: Record<string, any[]> = {};
  englishInterviews.forEach(i => {
    const title = i.jobTitle?.text || getStr(i.jobTitle || i.title || i.job || i.position || '');
    const dept = getDepartment(title);
    if (!interviewsByDept[dept]) interviewsByDept[dept] = [];
    interviewsByDept[dept].push({
      title: title || 'Unknown Role',
      difficulty: getStr(i.difficulty || i.difficultyRating || ''),
      experience: getStr(i.experience || i.overallExperience || ''),
      offer: getStr(i.offer || i.gotOffer || ''),
      questions: getStr(i.interviewQuestions || i.questions || i.question || ''),
      process: getStr(i.process || i.interviewProcess || i.description || '')
    });
  });

  const deptSummaries = Object.entries(interviewsByDept).map(([dept, ints]) => {
    const difficulties = ints.map(i => i.difficulty.toUpperCase()).filter(Boolean);
    const experiences = ints.map(i => i.experience.toUpperCase()).filter(Boolean);
    const avgDifficulty = difficulties.length ? 
      (difficulties.filter(d => d.includes('DIFFICULT')).length > difficulties.length/2 ? 'Difficult' :
       difficulties.filter(d => d.includes('EASY')).length > difficulties.length/2 ? 'Easy' : 'Average') : '';
    const positiveRate = experiences.length ? 
      Math.round(experiences.filter(e => e.includes('POSITIVE')).length / experiences.length * 100) : 0;
    return { dept, interviews: ints, avgDifficulty, positiveRate };
  }).sort((a, b) => b.interviews.length - a.interviews.length);

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h4 className="font-semibold text-green-400 mb-3">🎤 Interviews by Department ({englishInterviews.length} English)</h4>
      <div className="space-y-2">
        {deptSummaries.map((d, idx) => (
          <details key={idx} className="border border-gray-700 rounded">
            <summary className="flex items-center justify-between p-3 hover:bg-gray-700 cursor-pointer">
              <div className="flex items-center gap-3">
                <span className="text-white font-medium">{d.dept}</span>
                <span className="text-xs text-gray-500">{d.interviews.length} interviews</span>
              </div>
              <div className="flex items-center gap-3">
                {d.avgDifficulty && <span className="text-xs text-yellow-400">{d.avgDifficulty}</span>}
                {d.positiveRate > 0 && <span className="text-xs text-green-400">{d.positiveRate}% positive</span>}
              </div>
            </summary>
            <div className="border-t border-gray-700 p-3 space-y-3 bg-gray-900">
              {d.interviews.slice(0, 6).map((interview, iIdx) => (
                <div key={iIdx} className="border-b border-gray-800 pb-2 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">{esc(interview.title)}</span>
                    <div className="flex gap-2 text-xs">
                      {interview.difficulty && <span className="text-yellow-400">{interview.difficulty}</span>}
                      {interview.experience && <span className={interview.experience.toLowerCase().includes('positive') ? 'text-green-400' : 'text-red-400'}>{interview.experience}</span>}
                    </div>
                  </div>
                  {interview.questions && <p className="text-xs text-gray-400 mt-1">{esc(interview.questions.substring(0, 200))}{interview.questions.length > 200 ? '...' : ''}</p>}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function GlassdoorReviewsSection({ reviews }: { reviews: any[] }) {
  const englishReviews = reviews.filter(r => {
    const text = getStr(r.pros || r.positive || '') + ' ' + getStr(r.cons || r.negatives || '');
    return isEnglish(text);
  });

  if (englishReviews.length === 0) return null;

  const reviewsByDept: Record<string, any[]> = {};
  englishReviews.forEach(r => {
    const title = r.jobTitle?.text || getStr(r.jobTitle || r.title || '');
    const dept = getDepartment(title);
    if (!reviewsByDept[dept]) reviewsByDept[dept] = [];
    reviewsByDept[dept].push({
      jobTitle: title || 'Anonymous',
      headline: getStr(r.headline || r.title || r.summary || ''),
      rating: getStr(r.rating || r.ratingOverall || ''),
      pros: getStr(r.pros || r.positives || ''),
      cons: getStr(r.cons || r.negatives || '')
    });
  });

  const deptSummaries = Object.entries(reviewsByDept).map(([dept, revs]) => {
    const ratings = revs.map(r => parseFloat(r.rating)).filter(r => !isNaN(r));
    const avgRating = ratings.length ? (ratings.reduce((a,b) => a+b, 0) / ratings.length).toFixed(1) : '';
    return { dept, reviews: revs, avgRating };
  }).sort((a, b) => b.reviews.length - a.reviews.length);

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h4 className="font-semibold text-green-400 mb-3">📝 Reviews by Department ({englishReviews.length} English)</h4>
      <div className="space-y-2">
        {deptSummaries.map((d, idx) => (
          <details key={idx} className="border border-gray-700 rounded">
            <summary className="flex items-center justify-between p-3 hover:bg-gray-700 cursor-pointer">
              <div className="flex items-center gap-3">
                <span className="text-white font-medium">{d.dept}</span>
                <span className="text-xs text-gray-500">{d.reviews.length} reviews</span>
              </div>
              {d.avgRating && <span className="text-yellow-400 text-sm">⭐ {d.avgRating}</span>}
            </summary>
            <div className="border-t border-gray-700 p-3 space-y-3 bg-gray-900">
              {d.reviews.slice(0, 8).map((review, rIdx) => (
                <div key={rIdx} className="border-b border-gray-800 pb-2 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">{esc(review.headline)}</span>
                    {review.rating && <span className="text-xs text-yellow-400">⭐ {review.rating}</span>}
                  </div>
                  {review.jobTitle && <p className="text-xs text-blue-400">{esc(review.jobTitle)}</p>}
                  {review.pros && <p className="text-xs text-green-400 mt-1">👍 {esc(review.pros.substring(0, 150))}{review.pros.length > 150 ? '...' : ''}</p>}
                  {review.cons && <p className="text-xs text-red-400">👎 {esc(review.cons.substring(0, 150))}{review.cons.length > 150 ? '...' : ''}</p>}
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

// Consumer Intel Content
function ConsumerIntelContent({ data }: { data: any }) {
  return (
    <>
      {/* Trustpilot */}
      {data.trustpilot?.length > 0 ? (
        <TrustpilotSection reviews={data.trustpilot} />
      ) : (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-emerald-400 mb-2">🛡️ Trustpilot</h4>
          <p className="text-gray-500 text-sm">No Trustpilot data found</p>
        </div>
      )}

      {/* G2 */}
      {data.g2?.length > 0 ? (
        <G2Section products={data.g2} />
      ) : (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-orange-400 mb-2">📊 G2</h4>
          <p className="text-gray-500 text-sm">No G2 data found</p>
        </div>
      )}

      {/* Show raw data if nothing found */}
      {!data.trustpilot?.length && !data.g2?.length && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-gray-400 mb-2">Raw Data</h4>
          <pre className="text-xs text-gray-500 overflow-auto max-h-96">{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </>
  );
}

export function TrustpilotOnlyContent({ data }: { data: any }) {
  const reviews = Array.isArray(data?.trustpilot)
    ? data.trustpilot
    : Array.isArray(data)
      ? data
      : [];
  return reviews.length > 0 ? (
    <TrustpilotSection reviews={reviews} />
  ) : (
    <div className="bg-gray-800 rounded-lg p-4">
      <h4 className="font-semibold text-emerald-400 mb-2">🛡️ Trustpilot</h4>
      <p className="text-gray-500 text-sm">No Trustpilot data found</p>
    </div>
  );
}

export function G2MultiContent({ data }: { data: any }) {
  const items = Array.isArray(data?.g2)
    ? data.g2
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];
  const byPlatform = items.reduce((acc: Record<string, any[]>, item: any) => {
    const key = (item?.platform || item?.source || 'g2').toString().toLowerCase();
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});

  const renderCards = (list: any[]) => (
    <div className="space-y-3">
      {list.slice(0, 6).map((item, idx) => (
        <div key={idx} className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm text-white font-medium">{esc(item.product_name || item.productName || 'Unknown product')}</p>
              {item.title && <p className="text-xs text-blue-300 mt-1">{esc(item.title)}</p>}
              {(item.author || item.author_title) && (
                <p className="text-xs text-gray-400 mt-1">
                  {esc(item.author || '')}{item.author_title ? ` · ${esc(item.author_title)}` : ''}
                </p>
              )}
            </div>
            {item.rating && (
              <span className="text-sm font-semibold text-orange-300">★ {item.rating}</span>
            )}
          </div>
          {item.date_posted && (
            <p className="text-xs text-gray-500 mt-1">{new Date(item.date_posted).toLocaleDateString()}</p>
          )}
          {item.content && (
            <p className="text-xs text-gray-300 mt-2">{esc(String(item.content).substring(0, 260))}{String(item.content).length > 260 ? '...' : ''}</p>
          )}
          {(item.review_url || item.product_url) && (
            <a
              href={esc(item.review_url || item.product_url)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-400 hover:underline mt-2 inline-block"
            >
              View source →
            </a>
          )}
        </div>
      ))}
    </div>
  );

  const renderSection = (label: string, key: string) => {
    const list = byPlatform[key] || [];
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-orange-400">{label}</h4>
          <span className="text-xs text-gray-400">{list.length} items</span>
        </div>
        {list.length ? renderCards(list) : <p className="text-gray-500 text-sm">No {label} data found</p>}
      </div>
    );
  };

  return (
    <>
      {renderSection('G2', 'g2')}
      {renderSection('Capterra', 'capterra')}
      {renderSection('Gartner', 'gartner')}
      <details className="bg-gray-800 rounded-lg p-4">
        <summary className="font-semibold text-gray-400 cursor-pointer">Raw Data (JSON)</summary>
        <pre className="text-xs text-gray-500 overflow-auto max-h-96 mt-2">{JSON.stringify(data, null, 2)}</pre>
      </details>
    </>
  );
}

function summarizeInstagramData(data: unknown): {
  username?: string;
  fullName?: string;
  bio?: string;
  website?: string;
  followers?: number | string;
  following?: number | string;
  postsCount?: number | string;
  profileUrl?: string;
  avatar?: string;
  isVerified?: boolean;
  recentPosts: Array<{
    caption?: string;
    url?: string;
    likes?: number | string;
    comments?: number | string;
    date?: string;
    image?: string;
    video?: string;
    views?: number | string;
  }>;
} {
  const payload = data && typeof data === 'object' ? (data as Record<string, any>) : undefined;
  const arrayData = Array.isArray(data) ? data : [];
  const items = Array.isArray(payload?.results)
    ? payload.results
    : arrayData.length > 0 && Array.isArray((arrayData[0] as any)?.results)
      ? (arrayData[0] as any).results
      : Array.isArray(data)
        ? data
        : data && typeof data === 'object'
          ? [data]
          : [];
  const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
  const first = (items[0] as Record<string, any>) || {};
  const profile = first;

  const username = getStr(profile.username || profile.userName || profile.handle);
  const fullName = getStr(profile.fullName || profile.name || profile.nickName);
  const bio = getStr(profile.biography || profile.bio);
  const website = getStr(profile.externalUrl || profile.externalUrlShimmed || profile.website);
  const followers = profile.followersCount ?? profile.followers ?? undefined;
  const following = profile.followsCount ?? profile.following ?? undefined;
  const postsCount = profile.postsCount ?? profile.posts ?? profile.igtvVideoCount ?? undefined;
  const profileUrl = getStr(profile.url || profile._discovered_profile_url || profile.inputUrl);
  const avatar = getStr(profile.profilePicUrlHD || profile.profilePicUrl || profile.avatar || profile.profilePicture);
  const isVerified = Boolean(profile.verified || profile.isVerified);

  const postSource = Array.isArray(profile.latestPosts) && profile.latestPosts.length > 0
    ? profile.latestPosts
    : Array.isArray(profile.latestIgtvVideos) && profile.latestIgtvVideos.length > 0
      ? profile.latestIgtvVideos
      : [];
  const recentPosts = postSource.slice(0, 50).map((p: any) => ({
    caption: getStr(p.caption || p.text || p.description),
    url: getStr(p.url || p.postUrl),
    likes: p.likesCount ?? p.likes ?? p.likeCount ?? undefined,
    comments: p.commentsCount ?? p.comments ?? p.commentCount ?? undefined,
    views: p.videoViewCount ?? undefined,
    date: getStr(p.timestamp || p.date),
    image: Array.isArray(p.images) ? p.images[0] : p.displayUrl || p.imageUrl || p.thumbnail,
    video: p.videoUrl || p.video,
  }));

  return {
    username,
    fullName,
    bio,
    website,
    followers,
    following,
    postsCount,
    profileUrl,
    avatar,
    isVerified,
    recentPosts,
  };
}

export function InstagramContent({ data }: { data: any }) {
  const summary = summarizeInstagramData(data);
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-fuchsia-900/40 to-gray-900 border border-fuchsia-700/40 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
            {summary.avatar ? (
              <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">Avatar</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-fuchsia-300/80">Instagram Profile</p>
            <h4 className="text-lg font-semibold text-white">{summary.fullName || 'Instagram Profile'}</h4>
            <p className="text-sm text-gray-400 mt-1">{summary.bio || 'No bio found.'}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {summary.isVerified && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-fuchsia-600 text-white">Verified</span>
            )}
            {summary.username && (
              <span className="text-xs text-fuchsia-200">@{summary.username}</span>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Followers</span>
            <div>{summary.followers ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Following</span>
            <div>{summary.following ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Posts</span>
            <div>{summary.postsCount ?? 'Unknown'}</div>
          </div>
        </div>
        {summary.website && (
          <div className="mt-3 text-xs text-gray-400">Website: {summary.website}</div>
        )}
        {summary.profileUrl && (
          <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
            View profile
          </a>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
        {summary.recentPosts.length === 0 ? (
          <p className="text-sm text-gray-400">No posts captured.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {summary.recentPosts.map((post, idx) => (
              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                <div className="text-xs text-gray-400">
                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                </div>
                <div className="text-sm text-gray-200 mt-1">{post.caption || 'Post content unavailable'}</div>
                {(post.image || post.video) && (
                  <div className="mt-2 space-y-2">
                    {post.image && (
                      <img src={post.image} alt="" className="w-full rounded-md border border-gray-700" />
                    )}
                    {post.video && (
                      <video
                        src={post.video}
                        controls
                        preload="metadata"
                        className="w-full rounded-md border border-gray-700"
                      />
                    )}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                  {post.likes !== undefined && (
                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                  )}
                  {post.comments !== undefined && (
                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                  )}
                </div>
                {post.url && (
                  <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                    View post
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <details className="bg-gray-800 rounded-lg p-4">
        <summary className="font-semibold text-gray-400 cursor-pointer">Raw Data (JSON)</summary>
        <pre className="text-xs text-gray-500 overflow-auto max-h-96 mt-2">{JSON.stringify(data, null, 2)}</pre>
      </details>
    </div>
  );
}

function summarizeFacebookData(data: unknown): {
  title?: string;
  pageUrl?: string;
  likes?: number | string;
  followers?: number | string;
  category?: string;
  phone?: string;
  website?: string;
  address?: string;
  intro?: string;
  profilePictureUrl?: string;
  coverPhotoUrl?: string;
  posts: Array<{
    date?: string;
    text?: string;
    url?: string;
    likes?: number | string;
    comments?: number | string;
    shares?: number | string;
    views?: number | string;
    image?: string;
    video?: string;
  }>;
} {
  const payload = (data && typeof data === 'object') ? (data as Record<string, any>) : {};
  const items = Array.isArray(data)
    ? data
    : Array.isArray(payload.items)
      ? payload.items
      : [];
  const pageArray = Array.isArray(payload.page) ? payload.page : [];
  const postsArray = Array.isArray(payload.posts) ? payload.posts : [];
  const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
  const pageItem =
    pageArray[0] ||
    items.find((i: any) => i && (i.pageName || i.pageUrl || i.title || i.facebookUrl));
  const postItems =
    postsArray.length > 0
      ? postsArray
      : items.filter((i: any) => i && (i.post_id || i.post_url || i.text));
  const page = (pageItem as Record<string, any>) || {};

  const title = getStr(page.pageName || page.title || page.facebookUrl);
  const pageUrl = getStr(page.pageUrl || page.facebookUrl);
  const likes = page.likes ?? undefined;
  const followers = page.followers ?? undefined;
  const category = getStr(page.category || (Array.isArray(page.categories) ? page.categories.join(', ') : ''));
  const phone = getStr(page.phone);
  const website = getStr(page.website);
  const address = getStr(page.address);
  const intro = getStr(page.intro);
  const profilePictureUrl = getStr(page.profilePictureUrl);
  const coverPhotoUrl = getStr(page.coverPhotoUrl);

  const posts = postItems.slice(0, 50).map((p: any) => ({
    date: p.create_time ? new Date(p.create_time).toISOString() : '',
    text: getStr(p.text),
    url: getStr(p.post_url || p.postUrl || p.input),
    likes: p.like_count ?? p.likes ?? undefined,
    comments: p.comment_count ?? p.comments ?? undefined,
    shares: p.share_count ?? p.shares ?? undefined,
    views: p.view_count ?? p.play_count ?? undefined,
    image: Array.isArray(p.image_list) ? p.image_list[0] : undefined,
    video: Array.isArray(p.video_list) ? p.video_list[0] : undefined,
  }));

  return {
    title,
    pageUrl,
    likes,
    followers,
    category,
    phone,
    website,
    address,
    intro,
    profilePictureUrl,
    coverPhotoUrl,
    posts,
  };
}

export function FacebookContent({ data }: { data: any }) {
  const summary = summarizeFacebookData(data);
  const [showRawDrawer, setShowRawDrawer] = useState(false);
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-indigo-900/40 to-gray-900 border border-indigo-700/40 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
            {summary.profilePictureUrl ? (
              <img src={summary.profilePictureUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">Logo</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-indigo-300/80">Facebook Page</p>
            <h4 className="text-lg font-semibold text-white">{summary.title || 'Facebook Page'}</h4>
            <p className="text-sm text-gray-400 mt-1">{summary.intro || 'No intro found.'}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {summary.followers !== undefined && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-600 text-white">
                {summary.followers} followers
              </span>
            )}
            {summary.likes !== undefined && (
              <span className="text-xs text-indigo-200">{summary.likes} likes</span>
            )}
          </div>
        </div>
        {summary.coverPhotoUrl && (
          <div className="mt-4 rounded-lg overflow-hidden border border-gray-800">
            <img src={summary.coverPhotoUrl} alt="" className="w-full h-28 object-cover" />
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Category</span>
            <div>{summary.category || 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Phone</span>
            <div>{summary.phone || 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Website</span>
            <div>{summary.website || 'Unknown'}</div>
          </div>
        </div>
        {summary.address && <div className="mt-3 text-xs text-gray-400">Address: {summary.address}</div>}
        {summary.pageUrl && (
          <a className="text-xs text-indigo-300 hover:text-indigo-200 mt-2 inline-block" href={summary.pageUrl} target="_blank" rel="noreferrer">
            View page
          </a>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
        {summary.posts.length === 0 ? (
          <p className="text-sm text-gray-400">No posts captured.</p>
        ) : (
          <div className="space-y-3">
            {summary.posts.map((post, idx) => (
              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                <div className="text-xs text-gray-400">
                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                </div>
                <div className="text-sm text-gray-200 mt-1 line-clamp-3">{post.text || 'Post content unavailable'}</div>
                {(post.image || post.video) && (
                  <div className="mt-2 text-xs text-indigo-200">{post.video ? 'Video post' : 'Image post'}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                  {post.likes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>}
                  {post.comments !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>}
                  {post.shares !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>}
                  {post.views !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>}
                </div>
                {post.url && (
                  <a className="text-xs text-indigo-300 hover:text-indigo-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                    View post
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowRawDrawer((v) => !v)}
        className="text-xs text-gray-400 hover:text-gray-200 underline"
      >
        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRawDrawer && (
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-gray-300">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function summarizeYouTubeData(data: unknown): {
  channelName?: string;
  channelUrl?: string;
  channelAvatar?: string;
  subscribers?: number | string;
  totalViews?: number | string;
  totalVideos?: number | string;
  channelLocation?: string;
  recentVideos: Array<{
    title?: string;
    url?: string;
    date?: string;
    views?: number | string;
    likes?: number | string;
    comments?: number | string;
    thumbnail?: string;
    text?: string;
  }>;
} {
  const payload = data && typeof data === 'object' ? (data as Record<string, any>) : undefined;
  const items = Array.isArray(data)
    ? data
    : Array.isArray(payload?.results)
      ? payload?.results
      : Array.isArray(payload?.items)
        ? payload?.items
        : [];
  const cleaned = items.filter((item: any) => {
    const note = String(item?.note || '').toLowerCase();
    const error = String(item?.error || '');
    return error !== 'NO_RESULTS' && !note.includes('no results were collected');
  });
  const first = (cleaned[0] as Record<string, any>) || {};
  const about = first.aboutChannelInfo || {};
  const channelName = about.channelName || first.channelName;
  const channelUrl = about.channelUrl || first.channelUrl;
  const channelAvatar = about.channelAvatarUrl || first.channelAvatarUrl;
  const subscribers = about.numberOfSubscribers || first.numberOfSubscribers;
  const totalViews = about.channelTotalViews || first.channelTotalViews;
  const totalVideos = about.channelTotalVideos || first.channelTotalVideos;
  const channelLocation = about.channelLocation || first.channelLocation;
  const recentVideos = cleaned.slice(0, 50).map((v: any) => ({
    title: v.title,
    url: v.url,
    date: v.date,
    views: v.viewCount,
    likes: v.likes,
    comments: v.commentsCount,
    thumbnail: v.thumbnailUrl,
    text: v.text,
  }));
  return {
    channelName,
    channelUrl,
    channelAvatar,
    subscribers,
    totalViews,
    totalVideos,
    channelLocation,
    recentVideos,
  };
}

export function YouTubeContent({ data }: { data: any }) {
  const summary = summarizeYouTubeData(data);
  const [showRawDrawer, setShowRawDrawer] = useState(false);
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-red-900/40 to-gray-900 border border-red-700/40 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
            {summary.channelAvatar ? (
              <img src={summary.channelAvatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">Avatar</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-red-300/80">YouTube Channel</p>
            <h4 className="text-lg font-semibold text-white">
              {summary.channelName || 'YouTube Channel'}
            </h4>
            <p className="text-sm text-gray-400 mt-1">
              {summary.channelLocation || 'Location unknown'}
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Subscribers</span>
            <div>{summary.subscribers ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Total Views</span>
            <div>{summary.totalViews ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Videos</span>
            <div>{summary.totalVideos ?? 'Unknown'}</div>
          </div>
        </div>
        {summary.channelUrl && (
          <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={summary.channelUrl} target="_blank" rel="noreferrer">
            View channel
          </a>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-3">Recent Videos</p>
        {summary.recentVideos.length === 0 ? (
          <p className="text-sm text-gray-400">No videos captured.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {summary.recentVideos.map((video, idx) => (
              <div key={`${video.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                <div className="text-xs text-gray-400">
                  {video.date ? new Date(video.date).toLocaleDateString() : 'No date'}
                </div>
                <div className="text-sm text-gray-200 mt-1">
                  {video.title || video.text || 'Video'}
                </div>
                {video.thumbnail && (
                  <div className="mt-2">
                    <img src={video.thumbnail} alt="" className="w-full rounded-md border border-gray-700" />
                  </div>
                )}
                {video.text && (
                  <div className="mt-2 text-xs text-gray-300">{video.text}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                  {video.views !== undefined && (
                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {video.views}</span>
                  )}
                  {video.likes !== undefined && (
                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {video.likes}</span>
                  )}
                  {video.comments !== undefined && (
                    <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {video.comments}</span>
                  )}
                </div>
                {video.url && (
                  <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={video.url} target="_blank" rel="noreferrer">
                    View video
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowRawDrawer((v) => !v)}
        className="text-xs text-gray-400 hover:text-gray-200 underline"
      >
        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRawDrawer && (
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-gray-300">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

const normalizeTikTokItems = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload;
  const data = payload as any;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data?.results)) return data.data.results;
  if (Array.isArray(data?.content)) return data.content;
  if (data) return [data];
  return [];
};

const normalizeTikTokDate = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    return new Date(ms).toISOString();
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const num = Number(trimmed);
      if (Number.isFinite(num)) {
        const ms = num < 1e12 ? num * 1000 : num;
        return new Date(ms).toISOString();
      }
    }
    return trimmed;
  }
  return String(value);
};

function summarizeTikTokData(data: unknown): {
  username?: string;
  fullName?: string;
  bio?: string;
  website?: string;
  followers?: number | string;
  following?: number | string;
  videos?: number | string;
  hearts?: number | string;
  profileUrl?: string;
  avatar?: string;
  isVerified?: boolean;
  recentPosts: Array<{
    caption?: string;
    url?: string;
    likes?: number | string;
    comments?: number | string;
    shares?: number | string;
    views?: number | string;
    date?: string;
    image?: string;
    duration?: number | string;
    hashtags?: string[];
  }>;
} {
  const items = normalizeTikTokItems(data);
  const getStr = (v: any) =>
    typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  const first = (items[0] as Record<string, any>) || {};
  const meta = first.authorMeta || first.author || first.authorInfo || {};
  const stats = first.authorStats || first.stats || first.statistics || {};

  const username = getStr(
    meta.name || meta.username || meta.uniqueId || meta.userName || meta.handle
  );
  const fullName = getStr(meta.nickName || meta.nickname || meta.name || meta.fullName || meta.displayName);
  const bio = getStr(meta.signature || meta.bio || meta.description || first.desc);
  const website = getStr(meta.bioLink || meta.website || meta.url || meta.link);
  const followers = meta.fans ?? meta.followers ?? meta.followerCount ?? stats.followerCount ?? stats.followers ?? undefined;
  const following = meta.following ?? meta.followingCount ?? stats.followingCount ?? undefined;
  const videos = meta.video ?? meta.videoCount ?? stats.videoCount ?? undefined;
  const hearts = meta.heart ?? meta.heartCount ?? meta.likeCount ?? stats.heartCount ?? stats.diggCount ?? undefined;
  const profileUrl = getStr(meta.profileUrl || meta.url || first._discovered_profile_url || (username ? `https://www.tiktok.com/@${username}` : ''));
  const avatar = getStr(meta.originalAvatarUrl || meta.avatar || meta.avatarLarger || meta.avatarThumb);
  const isVerified = Boolean(meta.verified || meta.verifiedStatus);

  const recentPosts = items.slice(0, 50).map((p: any) => {
    const postUrl =
      getStr(p.webVideoUrl || p.url || p.videoUrl || p.video?.downloadAddr) ||
      (username && p.id ? `https://www.tiktok.com/@${username}/video/${p.id}` : '');
    const cover =
      p.videoMeta?.coverUrl ||
      p.videoMeta?.originalCoverUrl ||
      p.video?.cover ||
      p.video?.originCover ||
      p.imageUrl;
    const duration =
      p.videoMeta?.duration ??
      p.video?.duration ??
      p.video?.durationInSeconds ??
      undefined;
    const statsObj = p.stats || p.statistics || {};
    return {
      caption: getStr(p.text || p.desc || p.caption),
      url: postUrl,
      likes: p.diggCount ?? statsObj.diggCount ?? p.likes ?? undefined,
      comments: p.commentCount ?? statsObj.commentCount ?? p.comments ?? undefined,
      shares: p.shareCount ?? statsObj.shareCount ?? p.shares ?? undefined,
      views: p.playCount ?? statsObj.playCount ?? p.views ?? p.playCount ?? undefined,
      date: normalizeTikTokDate(p.createTimeISO ?? p.createTime ?? p.createTimeUnix ?? p.date ?? p.timestamp),
      image: cover,
      duration,
      hashtags: Array.isArray(p.hashtags)
        ? p.hashtags.map((h: any) => getStr(h.name || h)).filter(Boolean)
        : [],
    };
  });

  return {
    username,
    fullName,
    bio,
    website,
    followers,
    following,
    videos,
    hearts,
    profileUrl,
    avatar,
    isVerified,
    recentPosts,
  };
};

export function TikTokContent({ data }: { data: any }) {
  const summary = summarizeTikTokData(data);
  const [showRawDrawer, setShowRawDrawer] = useState(false);
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-cyan-900/40 to-gray-900 border border-cyan-700/40 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
            {summary.avatar ? (
              <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">Avatar</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-cyan-300/80">TikTok Profile</p>
            <h4 className="text-lg font-semibold text-white">
              {summary.fullName || 'TikTok Profile'}
            </h4>
            <p className="text-sm text-gray-400 mt-1">
              {summary.bio || 'No bio found.'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {summary.isVerified && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-cyan-600 text-white">
                Verified
              </span>
            )}
            {summary.username && (
              <span className="text-xs text-cyan-200">@{summary.username}</span>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-300">
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Followers</span>
            <div>{summary.followers ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Following</span>
            <div>{summary.following ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Videos</span>
            <div>{summary.videos ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Hearts</span>
            <div>{summary.hearts ?? 'Unknown'}</div>
          </div>
        </div>
        {summary.website && (
          <div className="mt-3 text-xs text-gray-400">Website: {summary.website}</div>
        )}
        {summary.profileUrl && (
          <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
            View profile
          </a>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
        {summary.recentPosts.length === 0 ? (
          <p className="text-sm text-gray-400">No posts captured.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {summary.recentPosts.map((post, idx) => (
              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                <div className="text-xs text-gray-400">
                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                </div>
                <div className="text-sm text-gray-200 mt-1 line-clamp-3">{post.caption || 'Post content unavailable'}</div>
                {(post.image || post.duration) && (
                  <div className="mt-2 text-xs text-cyan-200">{post.duration ? 'Video post' : 'Image post'}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                  {post.likes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>}
                  {post.comments !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>}
                  {post.shares !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>}
                  {post.views !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>}
                </div>
                {post.url && (
                  <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                    View post
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowRawDrawer((v) => !v)}
        className="text-xs text-gray-400 hover:text-gray-200 underline"
      >
        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRawDrawer && (
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-gray-300">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function summarizeXData(data: unknown): {
  profileName?: string;
  profileHandle?: string;
  bio?: string;
  followers?: number | string;
  following?: number | string;
  avatar?: string;
  profileUrl?: string;
  recentPosts: Array<{
    text?: string;
    url?: string;
    date?: string | number;
    likes?: number | string;
    replies?: number | string;
    reposts?: number | string;
    quotes?: number | string;
    media?: string;
    repliesList?: Array<Record<string, any>>;
  }>;
} {
  const items = Array.isArray(data) ? data : [];
  const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
  const first = (items[0] as Record<string, any>) || {};
  const author = first.author || first.user || {};

  const profileName = getStr(author.name || author.fullName || author.displayName);
  const profileHandle = getStr(author.username || author.screen_name || author.screenName || author.handle);
  const bio = getStr(author.description || author.bio);
  const followers = author.followers_count ?? author.followers ?? author.followersCount ?? undefined;
  const following = author.friends_count ?? author.following ?? author.friendsCount ?? undefined;
  const avatar = getStr(author.profile_image_url_https || author.profileImageUrl || author.avatar);
  const profileUrl = profileHandle ? `https://x.com/${profileHandle}` : getStr(author.profileUrl);

  const recentPosts = items.slice(0, 50).map((post: any) => {
    const repliesList = Array.isArray(post.replies)
      ? post.replies
      : Array.isArray(post.comments)
        ? post.comments
        : Array.isArray(post.replies?.items)
          ? post.replies.items
          : undefined;
    return {
      text: getStr(post.full_text || post.text || post.caption || post.postText),
      url: getStr(post.url || post.tweetUrl || post.post_url || post.postUrl),
      date: post.created_at ?? post.date ?? post.timestamp ?? post.createdAt,
      likes: post.favorite_count ?? post.favouriteCount ?? post.likes ?? post.favouritesCount ?? undefined,
      replies: post.reply_count ?? post.replyCount ?? post.replies ?? undefined,
      reposts: post.retweet_count ?? post.repostCount ?? post.reposts ?? undefined,
      quotes: post.quote_count ?? post.quoteCount ?? post.quotes ?? undefined,
      media: Array.isArray(post.media)
        ? post.media[0]?.media_url_https || post.media[0]?.mediaUrlHttps || post.media[0]?.mediaUrl || post.media[0]?.url
        : undefined,
      repliesList,
    };
  });

  return {
    profileName,
    profileHandle,
    bio,
    followers,
    following,
    avatar,
    profileUrl,
    recentPosts,
  };
}

export function XContent({ data }: { data: any }) {
  const summary = summarizeXData(data);
  const [showRawDrawer, setShowRawDrawer] = useState(false);
  const formatDate = (value?: string | number) => {
    if (!value) return 'No date';
    let date: Date;
    if (typeof value === 'number') {
      const ms = value > 1e12 ? value : value > 1e9 ? value * 1000 : value;
      date = new Date(ms);
    } else if (typeof value === 'string' && /^\d+$/.test(value)) {
      const num = Number(value);
      const ms = num > 1e12 ? num : num > 1e9 ? num * 1000 : num;
      date = new Date(ms);
    } else {
      date = new Date(value);
    }
    return Number.isNaN(date.getTime()) ? 'No date' : date.toLocaleDateString();
  };
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-slate-800/60 to-gray-900 border border-slate-600/40 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
            {summary.avatar ? (
              <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">Avatar</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-slate-300/80">X Profile</p>
            <h4 className="text-lg font-semibold text-white">
              {summary.profileName || 'X Profile'}
            </h4>
            {summary.profileHandle && (
              <p className="text-sm text-gray-400 mt-1">@{summary.profileHandle}</p>
            )}
            {summary.bio && (
              <p className="text-sm text-gray-300 mt-2">{summary.bio}</p>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-300">
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Followers</span>
            <div>{summary.followers ?? 'Unknown'}</div>
          </div>
          <div className="bg-gray-900/60 rounded-lg p-2">
            <span className="text-gray-500">Following</span>
            <div>{summary.following ?? 'Unknown'}</div>
          </div>
        </div>
        {summary.profileUrl && (
          <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
            View profile
          </a>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
        {summary.recentPosts.length === 0 ? (
          <p className="text-sm text-gray-400">No posts captured.</p>
        ) : (
          <div className="space-y-3">
            {summary.recentPosts.map((post, idx) => (
              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                <div className="text-xs text-gray-400">
                  {formatDate(post.date)}
                </div>
                {post.media && (
                  <div className="mt-2">
                    <img src={post.media} alt="" className="w-full max-w-sm rounded-md border border-gray-700" />
                  </div>
                )}
                <div className="text-sm text-gray-200 mt-1">
                  {post.text || 'Post content unavailable'}
                </div>
                {Array.isArray(post.repliesList) && post.repliesList.length > 0 && (
                  <details className="mt-2 text-xs text-gray-300">
                    <summary className="cursor-pointer text-gray-400">Replies ({post.repliesList.length})</summary>
                    <div className="mt-2 space-y-2">
                      {post.repliesList.slice(0, 5).map((reply, rIdx) => {
                        const replyText =
                          reply.text ||
                          reply.full_text ||
                          reply.postText ||
                          reply.comment ||
                          reply.content ||
                          '';
                        const replyAuthor =
                          reply.author?.name ||
                          reply.user?.name ||
                          reply.username ||
                          reply.authorName ||
                          '';
                        return (
                          <div key={`${post.url}-reply-${rIdx}`} className="bg-gray-900/60 rounded p-2">
                            {replyAuthor && <div className="text-gray-400 text-[11px]">{replyAuthor}</div>}
                            <div className="text-gray-200 text-xs">{replyText || 'Reply content unavailable'}</div>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                  {post.likes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>}
                  {post.replies !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Replies {post.replies}</span>}
                  {post.reposts !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Reposts {post.reposts}</span>}
                  {post.quotes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Quotes {post.quotes}</span>}
                </div>
                {post.url && (
                  <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                    View post
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowRawDrawer((v) => !v)}
        className="text-xs text-gray-400 hover:text-gray-200 underline"
      >
        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRawDrawer && (
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-gray-300">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function summarizeThreadsData(data: unknown[]): {
  profileName?: string;
  profileHandle?: string;
  avatar?: string;
  bio?: string;
  profileUrl?: string;
  recentPosts: Array<{
    text?: string;
    url?: string;
    date?: string;
    likes?: number | string;
    replies?: number | string;
    reposts?: number | string;
    image?: string;
    video?: string;
  }>;
} {
  const items = Array.isArray(data) ? data : [];
  const first = (items[0] as Record<string, any>) || {};
  const author = first.author || first.user || first.profile || {};
  const profileName = getStr(author.name || author.fullName || author.displayName || first.fullName);
  const profileHandle = getStr(author.username || author.handle || first.username);
  const avatar = getStr(author.profilePicUrl || author.avatar || author.profile_pic_url || first.profilePicUrl);
  const bio = getStr(author.biography || author.bio || first.biography);
  const profileUrl = getStr(author.profileUrl || first.profileUrl || (profileHandle ? `https://www.threads.com/@${profileHandle}` : ''));
  const recentPosts = items.slice(0, 50).map((post: any) => {
    const takenAt = post.taken_at ?? post.timestamp ?? post.created_at ?? post.createdAt;
    const date =
      typeof takenAt === 'number'
        ? new Date(takenAt * 1000).toISOString()
        : getStr(takenAt);
    const code = getStr(post.code);
    const url =
      getStr(post.url || post.postUrl || post.post_url) ||
      (profileHandle && code ? `https://www.threads.com/@${profileHandle}/post/${code}` : '');
    const imageCandidates = post.image_versions2?.candidates;
    const image =
      Array.isArray(imageCandidates) && imageCandidates.length > 0
        ? imageCandidates[0]?.url
        : undefined;
    const videoCandidates = post.video_versions;
    const video =
      Array.isArray(videoCandidates) && videoCandidates.length > 0
        ? videoCandidates[0]?.url
        : undefined;
    const repliesRaw = post.reply_count ?? post.replyCount ?? post.repliesCount ?? post.replies;
    const replies =
      typeof repliesRaw === 'string'
        ? Number.parseInt(repliesRaw, 10) || undefined
        : repliesRaw;
    return {
      text: getStr(post.text || post.caption?.text || post.caption || post.content || post.postText),
      url,
      date,
      likes: post.like_count ?? post.likes ?? post.likeCount ?? post.likesCount,
      replies,
      reposts: post.reposts ?? post.repostCount ?? post.repostsCount,
      image,
      video,
    };
  });
  return { profileName, profileHandle, avatar, bio, profileUrl, recentPosts };
}

export function ThreadsContent({ data }: { data: any }) {
  const summary = summarizeThreadsData(Array.isArray(data) ? data : []);
  const [showRawDrawer, setShowRawDrawer] = useState(false);
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-br from-slate-800/60 to-gray-900 border border-slate-600/40 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
            {summary.avatar ? (
              <img src={summary.avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-gray-400">Avatar</span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-slate-300/80">Threads Profile</p>
            <h4 className="text-lg font-semibold text-white">
              {summary.profileName || 'Threads Profile'}
            </h4>
            {summary.profileHandle && (
              <p className="text-sm text-gray-400 mt-1">@{summary.profileHandle}</p>
            )}
            {summary.bio && (
              <p className="text-sm text-gray-300 mt-2 line-clamp-3">{summary.bio}</p>
            )}
          </div>
        </div>
        {summary.profileUrl && (
          <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={summary.profileUrl} target="_blank" rel="noreferrer">
            View profile
          </a>
        )}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        <p className="text-xs uppercase text-gray-500 mb-3">Recent Posts</p>
        {summary.recentPosts.length === 0 ? (
          <p className="text-sm text-gray-400">No posts captured.</p>
        ) : (
          <div className="space-y-3">
            {summary.recentPosts.map((post, idx) => (
              <div key={`${post.url}-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                <div className="text-xs text-gray-400">
                  {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                </div>
                <div className="text-sm text-gray-200 mt-1 line-clamp-3">
                  {post.text || 'Post content unavailable'}
                </div>
                {(post.image || post.video) && (
                  <div className="mt-2 text-xs text-slate-200">{post.video ? 'Video post' : 'Image post'}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                  {post.likes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>}
                  {post.replies !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Replies {post.replies}</span>}
                  {post.reposts !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Reposts {post.reposts}</span>}
                </div>
                {post.url && (
                  <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                    View post
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowRawDrawer((v) => !v)}
        className="text-xs text-gray-400 hover:text-gray-200 underline"
      >
        {showRawDrawer ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRawDrawer && (
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-xs text-gray-300">{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function TrustpilotSection({ reviews }: { reviews: any[] }) {
  const companyName = reviews[0]?.companyName || '';
  const ratings = reviews.map(r => r.reviewRatingScore).filter(r => r);
  const avgRating = ratings.length > 0 ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : null;
  const englishReviews = reviews.filter(r => r.reviewLanguage === 'en' || !r.reviewLanguage);

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-emerald-400">🛡️ Trustpilot</h4>
        {avgRating && <span className="text-2xl font-bold text-emerald-400">★ {avgRating}</span>}
      </div>
      {companyName && <p className="text-sm text-white mb-1">{esc(companyName)}</p>}
      <p className="text-sm text-gray-400 mb-3">{reviews.length} reviews scraped</p>
      
      {englishReviews.length > 0 && (
        <div className="border-t border-gray-700 pt-3 mt-3">
          <p className="text-xs text-gray-500 mb-2">Recent Reviews:</p>
          {englishReviews.slice(0, 8).map((r, idx) => (
            <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
              <div className="flex justify-between items-center">
                <span className="text-xs text-blue-400">{esc(r.reviewer || '')}</span>
                <div className="flex items-center gap-2">
                  {r.reviewRatingScore && <span className="text-yellow-400 text-xs">★ {r.reviewRatingScore}/5</span>}
                  {r.reviewDate && <span className="text-gray-500 text-xs">{new Date(r.reviewDate).toLocaleDateString()}</span>}
                </div>
              </div>
              {r.reviewTitle && <p className="text-sm text-white font-medium mt-1">{esc(r.reviewTitle)}</p>}
              <p className="text-xs text-gray-300 mt-1">{esc((r.reviewDescription || '').substring(0, 250))}{(r.reviewDescription || '').length > 250 ? '...' : ''}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function G2Section({ products }: { products: any[] }) {
  const topProduct = products[0];

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-orange-400">📊 G2</h4>
        {topProduct.ratingOutOfFive && <span className="text-2xl font-bold text-orange-400">★ {topProduct.ratingOutOfFive}</span>}
      </div>
      {topProduct.productName && <p className="text-sm text-white font-medium mb-1">{esc(topProduct.productName)}</p>}
      {topProduct.vendorName && <p className="text-xs text-blue-400 mb-2">{esc(topProduct.vendorName)}</p>}
      {topProduct.reviewCount && <p className="text-sm text-gray-400 mb-2">{topProduct.reviewCount.toLocaleString()} reviews</p>}
      {topProduct.descriptionSnippet && <p className="text-xs text-gray-300 mb-3">{esc(topProduct.descriptionSnippet.substring(0, 300))}{topProduct.descriptionSnippet.length > 300 ? '...' : ''}</p>}
      
      {topProduct.relatedCategories?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {topProduct.relatedCategories.slice(0, 5).map((cat: string, idx: number) => (
            <span key={idx} className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs rounded">{esc(cat)}</span>
          ))}
        </div>
      )}
      
      {topProduct.reviewsUrl && <a href={esc(topProduct.reviewsUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">View on G2 →</a>}
      
      {products.length > 1 && (
        <div className="border-t border-gray-700 pt-3 mt-3">
          <p className="text-xs text-gray-500 mb-2">Related Products ({products.length - 1} more):</p>
          {products.slice(1, 4).map((p, idx) => (
            <div key={idx} className="flex justify-between items-center border-b border-gray-700 py-2 last:border-0">
              <span className="text-xs text-white">{esc(p.productName || '')}</span>
              <div className="flex items-center gap-2">
                {p.ratingOutOfFive && <span className="text-yellow-400 text-xs">★ {p.ratingOutOfFive}</span>}
                {p.reviewCount && <span className="text-gray-500 text-xs">{p.reviewCount} reviews</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Crunchbase Content
export function CrunchbaseContent({ data, companyName }: { data: any; companyName: string }) {
  // Crunchbase can be nested in multiple wrapper layers (items/data/output/result)
  const decodeHtmlEntities = (value: string): string =>
    value
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');

  const parseMaybeJson = (val: any): any => {
    if (typeof val !== 'string') return val;
    const trimmed = decodeHtmlEntities(val).trim();
    if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return val;
    try {
      return JSON.parse(trimmed);
    } catch {
      return val;
    }
  };

  const unwrapOnce = (val: any): any => {
    if (!val) return val;
    if (Array.isArray(val)) return val[0];
    if (val.items) return Array.isArray(val.items) ? val.items[0] : val.items;
    if (val.data) return Array.isArray(val.data) ? val.data[0] : val.data;
    if (val.output) return Array.isArray(val.output) ? val.output[0] : val.output;
    if (val.result) return Array.isArray(val.result) ? val.result[0] : val.result;
    return val;
  };

  const rawData = parseMaybeJson(data);
  let cbData = unwrapOnce(rawData);
  for (let i = 0; i < 4; i++) {
    const next = unwrapOnce(cbData);
    if (next === cbData) break;
    cbData = next;
  }

  console.log('Crunchbase data (raw):', rawData);
  console.log('Crunchbase data (normalized):', cbData);

  // If we still don't have a good-looking object, search for the richest candidate.
  const looksLikeCrunchbase = (val: any): number => {
    if (!val || typeof val !== 'object' || Array.isArray(val)) return 0;
    const keys = [
      'properties',
      'overview_fields_extended',
      'funding_rounds_summary',
      'key_employee_change_list',
      'partnership_announcements',
      'bombora_surge_list',
      'builtwith_tech_used_list',
      'siftery_product_list',
      'apptopia_app_overview_list_public',
      'acquisitions_list',
      'product_launches',
      'awards',
      'layoff_list',
    ];
    return keys.reduce((score, k) => score + (k in val ? 1 : 0), 0);
  };

  if (looksLikeCrunchbase(cbData) === 0) {
    const candidates: any[] = [];
    const visit = (val: any, depth: number) => {
      if (!val || depth > 3) return;
      if (Array.isArray(val)) {
        for (const item of val) visit(item, depth + 1);
        return;
      }
      if (typeof val === 'object') {
        candidates.push(val);
        for (const k of Object.keys(val)) {
          visit(val[k], depth + 1);
        }
      }
    };
    visit(rawData, 0);
    candidates.sort((a, b) => looksLikeCrunchbase(b) - looksLikeCrunchbase(a));
    if (candidates[0] && looksLikeCrunchbase(candidates[0]) > 0) {
      cbData = candidates[0];
    }
  }

  // Some connectors stash full payload under raw/payload/response fields
  const rawPayload = parseMaybeJson(
    (cbData as any)?.raw ??
      (cbData as any)?.payload ??
      (cbData as any)?.response ??
      (rawData as any)?.raw ??
      (rawData as any)?.payload ??
      (rawData as any)?.response
  );
  if (looksLikeCrunchbase(rawPayload) > looksLikeCrunchbase(cbData)) {
    cbData = rawPayload;
  }
  
  if (!cbData) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <p className="text-gray-500">No Crunchbase data available</p>
      </div>
    );
  }

  // Extract company info from nested structure
  const name = cbData.properties?.identifier?.value || cbData.properties?.title || cbData.name || companyName;
  const description = cbData.overview_description?.description || cbData.about_short_description?.short_description || cbData.properties?.short_description || cbData.description || '';
  
  // Extract founded year from description or other fields
  const foundedMatch = description.match(/founded in (\d{4})/i);
  const founded = foundedMatch ? foundedMatch[1] : (cbData.founded_on || cbData.foundedDate || '');
  
  // Location
  const locationGroups = cbData.overview_fields_extended?.location_group_identifiers || [];
  const hq = locationGroups.length > 0 ? locationGroups[0].value : (cbData.headquarters || cbData.location || '');
  
  // Categories/Industries
  const categories = cbData.overview_fields_extended?.categories || cbData.categories || [];
  
  // Stock info
  const stockSymbol = cbData.company_financials_highlights?.listed_stock_symbol || cbData.overview_company_fields?.stock_link?.label || cbData.stock_symbol || '';
  const stockUrl = cbData.overview_company_fields?.stock_link?.value || '';
  
  // Operating status
  const status = cbData.overview_fields_extended?.operating_status || cbData.status || '';

  // Growth & Heat
  const growthAndHeat = cbData.growth_and_heat || cbData.growthAndHeat || {};
  const growthScore = growthAndHeat.growth_score ?? cbData.growth_score ?? null;
  const heatScore = growthAndHeat.heat_score ?? cbData.heat_score ?? null;
  const growthDelta90 = growthAndHeat.growth_score_delta_d90 ?? null;
  const heatDelta90 = growthAndHeat.heat_score_delta_d90 ?? null;

  // Funding info
  const fundingSummary = cbData.funding_rounds_summary || {};
  const financials = cbData.company_financials_highlights || {};
  const numRounds = fundingSummary.num_funding_rounds || financials.num_funding_rounds || cbData.num_funding_rounds || 0;
  const lastFundingType = cbData.overview_fields_extended?.last_funding_type || fundingSummary.last_funding_type || cbData.last_funding_type || '';
  const numInvestors = financials.num_investors || cbData.num_investors || 0;
  const totalFunding = cbData.total_funding_usd || cbData.funding_total?.value_usd || financials.total_funding_usd || 0;
  
  // Investors
  const investorsList = cbData.investors_list || cbData.investors || [];
  const uniqueInvestors = [...new Set(investorsList.map((i: any) => i.investor_identifier?.value || i.name || i).filter(Boolean))] as string[];
  
  // Leadership
  const leadership = cbData.current_employees_featured_order_field || cbData.leadership || cbData.people || [];
  
  // Founders
  const founders = cbData.overview_fields_extended?.founder_identifiers || cbData.founders || [];
  
  // Executive changes
  const execChanges = cbData.key_employee_change_list || [];
  
  // Products
  const products = cbData.product || cbData.products || [];
  
  // Partnerships
  const partnerships = cbData.partnership_announcements || [];

  // Predictions
  const ipoPrediction = cbData.ipo_prediction || [];
  const acquisitionPrediction = cbData.acquisition_prediction || [];
  const growthPrediction = cbData.growth_prediction || [];

  // Acquisitions
  const acquisitionsSummary = cbData.acquisitions_summary || {};
  const acquisitionsList = cbData.acquisitions_list || [];

  // IP / Trademark
  const ipqwerySummary = cbData.ipqwery_summary || {};

  // Bombora
  const bomboraSummary = cbData.bombora_summary || {};
  const bomboraSurgeList = cbData.bombora_surge_list || [];

  // Awards & Layoffs
  const awards = cbData.awards || [];
  const layoffs = cbData.layoff_list || [];

  // Similar companies / hubs
  const recommendedSearch = cbData.recommended_search || [];
  const orgSimilarityList = cbData.org_similarity_list || [];
  const orgSimilarityOrgList = cbData.org_similarity_org_list || [];

  // Contacts / People
  const contacts = cbData.contacts || {};
  const contactFields = cbData.contact_fields || {};
  const advisorsSummary = cbData.advisors_summary || {};
  const currentEmployeesSummary = cbData.current_employees_summary || {};
  const peopleHighlights = cbData.people_highlights || {};

  // Product launches
  const productLaunches = cbData.product_launches || [];

  // SEMrush
  const semrushRankHeadline = cbData.semrush_rank_headline || {};
  const semrushAttribution = cbData.semrush_attribution || {};

  // BuiltWith
  const builtwithSummary = cbData.builtwith_summary || {};
  const builtwithTechList = cbData.builtwith_tech_used_list || [];

  // Siftery
  const sifterySummary = cbData.siftery_summary || {};
  const sifteryProducts = cbData.siftery_product_list || [];

  // Apptopia
  const apptopiaSummary = cbData.apptopia_summary || {};
  const apptopiaHeadline = cbData.apptopia_app_headline || {};
  const apptopiaApps = cbData.apptopia_app_overview_list_public || [];

  const alwaysShowSections = true;
  const showEmpty = (message: string) => (
    <p className="text-sm text-gray-400">{message}</p>
  );
  const formatDate = (value?: string | number) => {
    if (!value) return '';
    let date: Date;
    if (typeof value === 'number') {
      const ms = value > 1e12 ? value : value > 1e9 ? value * 1000 : value;
      date = new Date(ms);
    } else if (typeof value === 'string' && /^\d+$/.test(value)) {
      const num = Number(value);
      const ms = num > 1e12 ? num : num > 1e9 ? num * 1000 : num;
      date = new Date(ms);
    } else {
      date = new Date(value);
    }
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const formatProbabilityScore = (value: unknown): string => {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return '';
    const normalized = num <= 1 ? num * 100 : num;
    return `${normalized.toFixed(2)}%`;
  };

  return (
    <>
      {/* Company Overview */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h4 className="font-semibold text-purple-400 mb-3">🏢 Company Overview</h4>
        <p className="text-xl text-white font-bold mb-2">{esc(name)}</p>
        {(growthScore !== null || heatScore !== null) && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            {growthScore !== null && (
              <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Growth Score</p>
                <p className="text-2xl font-bold text-green-400">{growthScore}</p>
                {growthDelta90 !== null && (
                  <p className={`text-xs ${growthDelta90 >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    90d: {growthDelta90 >= 0 ? '+' : ''}{growthDelta90}
                  </p>
                )}
              </div>
            )}
            {heatScore !== null && (
              <div className="bg-black/30 border border-gray-700 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Heat Score</p>
                <p className="text-2xl font-bold text-orange-400">{heatScore}</p>
                {heatDelta90 !== null && (
                  <p className={`text-xs ${heatDelta90 >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    90d: {heatDelta90 >= 0 ? '+' : ''}{heatDelta90}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        {description && <p className="text-sm text-gray-300 mb-3">{esc(description.substring(0, 400))}{description.length > 400 ? '...' : ''}</p>}
        <div className="grid grid-cols-2 gap-2 text-sm">
          {founded && <div><span className="text-gray-500">Founded:</span> <span className="text-white">{esc(String(founded))}</span></div>}
          {hq && <div><span className="text-gray-500">HQ:</span> <span className="text-white">{esc(hq)}</span></div>}
          {status && <div><span className="text-gray-500">Status:</span> <span className={status === 'active' ? 'text-green-400' : 'text-red-400'}>{esc(status)}</span></div>}
          {stockSymbol && (
            <div>
              <span className="text-gray-500">Stock:</span>{' '}
              {stockUrl ? <a href={esc(stockUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{esc(stockSymbol)}</a> : <span className="text-white">{esc(stockSymbol)}</span>}
            </div>
          )}
        </div>
        {categories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {categories.slice(0, 6).map((c: any, idx: number) => (
              <span key={idx} className="px-2 py-1 bg-purple-900/30 text-purple-300 text-xs rounded">{esc(c.value || c.name || String(c))}</span>
            ))}
          </div>
        )}
      </div>

      {/* Funding Info */}
      {(numRounds > 0 || uniqueInvestors.length > 0 || totalFunding > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">💰 Funding</h4>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {totalFunding > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">${(totalFunding / 1000000).toFixed(1)}M</p>
                <p className="text-xs text-gray-500">Total Raised</p>
              </div>
            )}
            {numRounds > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-400">{numRounds}</p>
                <p className="text-xs text-gray-500">Rounds</p>
              </div>
            )}
            {numInvestors > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-400">{numInvestors}</p>
                <p className="text-xs text-gray-500">Investors</p>
              </div>
            )}
          </div>
          {lastFundingType && <p className="text-sm text-gray-400">Last funding: <span className="text-white">{esc(lastFundingType.replace(/_/g, ' '))}</span></p>}
          
          {uniqueInvestors.length > 0 && (
            <div className="mt-3 border-t border-gray-700 pt-3">
              <p className="text-xs text-gray-500 mb-2">Notable Investors:</p>
              <div className="flex flex-wrap gap-1">
                {uniqueInvestors.slice(0, 10).map((inv, idx) => (
                  <span key={idx} className="px-2 py-1 bg-green-900/30 text-green-300 text-xs rounded">{esc(inv)}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* IPO / Acquisition / Growth Predictions */}
      {(alwaysShowSections || ipoPrediction.length > 0 || acquisitionPrediction.length > 0 || growthPrediction.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🔮 Predictions</h4>
          {(() => {
            const renderPredictionList = (label: string, list: any[]) => {
              if (!list.length) return null;
              return (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 mb-2">{label}</div>
                  <div className="space-y-2">
                    {list.slice(0, 5).map((item: any, idx: number) => (
                      <div key={`${label}-${idx}`} className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                        {item?.probability_score !== undefined || item?.probability_tier || item?.generated_on ? (
                          <>
                            <div className="text-sm font-medium text-white">
                              {esc(label.replace(/s$/, ''))}
                              {item?.generated_on ? ` (generated ${formatDate(item.generated_on)})` : ''}
                            </div>
                            {item?.probability_score !== undefined && (
                              <div className="text-sm text-gray-200 mt-2">
                                Probability score: <span className="text-emerald-300">{esc(formatProbabilityScore(item.probability_score))}</span>
                              </div>
                            )}
                            {item?.probability_tier && (
                              <div className="text-sm text-gray-300 mt-1">
                                Tier: <span className="text-white">{esc(String(item.probability_tier))}</span>
                              </div>
                            )}
                            {(item?.created_at || item?.updated_at) && (
                              <div className="text-xs text-gray-500 mt-2">
                                {item?.created_at ? `Created ${formatDate(item.created_at)}` : ''}
                                {item?.created_at && item?.updated_at ? ' and ' : ''}
                                {item?.updated_at ? `updated ${formatDate(item.updated_at)}` : ''}
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-3">
                              <div className="text-sm text-white">
                                {esc(String(
                                  item?.prediction ||
                                  item?.label ||
                                  item?.name ||
                                  item?.title ||
                                  item?.value ||
                                  item?.prediction_type ||
                                  'Prediction'
                                ))}
                              </div>
                              {(item?.score ?? item?.confidence ?? item?.probability ?? item?.likelihood ?? item?.percent) !== undefined && (
                                <div className="text-xs text-emerald-300">
                                  Score: {esc(String(item?.score ?? item?.confidence ?? item?.probability ?? item?.likelihood ?? item?.percent))}
                                </div>
                              )}
                            </div>
                            {(item?.date || item?.prediction_date || item?.as_of_date || item?.timestamp || item?.timeframe) && (
                              <div className="text-xs text-gray-500 mt-1">
                                As of {esc(String(item?.date || item?.prediction_date || item?.as_of_date || item?.timestamp || item?.timeframe))}
                              </div>
                            )}
                            {item?.description && (
                              <div className="text-xs text-gray-300 mt-2">{esc(String(item.description).slice(0, 220))}</div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            };

            return (
              <>
                <div className="space-y-1 text-sm text-gray-300">
                  <div>Growth Prediction: {growthPrediction.length > 0 ? `${growthPrediction.length} record${growthPrediction.length === 1 ? '' : 's'}` : 'Empty'}</div>
                  <div>Acquisition Prediction: {acquisitionPrediction.length > 0 ? `${acquisitionPrediction.length} record${acquisitionPrediction.length === 1 ? '' : 's'}` : 'Empty'}</div>
                  <div>IPO Prediction: {ipoPrediction.length > 0 ? `${ipoPrediction.length} record${ipoPrediction.length === 1 ? '' : 's'}` : 'Empty'}</div>
                </div>
                {ipoPrediction.length === 0 && acquisitionPrediction.length === 0 && growthPrediction.length === 0
                  ? showEmpty('No predictions available')
                  : null}
                {renderPredictionList('Growth Predictions', growthPrediction)}
                {renderPredictionList('Acquisition Predictions', acquisitionPrediction)}
                {renderPredictionList('IPO Predictions', ipoPrediction)}
                <details className="mt-4">
                  <summary className="text-xs text-gray-500 cursor-pointer">Raw predictions JSON</summary>
                  <pre className="mt-2 text-xs text-gray-300 whitespace-pre-wrap bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                    {esc(JSON.stringify({
                      growth_prediction: growthPrediction,
                      acquisition_prediction: acquisitionPrediction,
                      ipo_prediction: ipoPrediction,
                    }, null, 2))}
                  </pre>
                </details>
              </>
            );
          })()}
        </div>
      )}

      {/* Acquisitions */}
      {(alwaysShowSections || acquisitionsSummary?.num_acquisitions || acquisitionsList.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🤝 Acquisitions</h4>
          {acquisitionsSummary?.num_acquisitions ? (
            <p className="text-sm text-gray-300 mb-2">Total acquisitions: {acquisitionsSummary.num_acquisitions}</p>
          ) : null}
          {acquisitionsList.length > 0 ? (
            acquisitionsList.slice(0, 5).map((a: any, idx: number) => (
              <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                <p className="text-sm text-white">
                  {esc(a.acquiree_identifier?.value || a.identifier?.value || 'Acquisition')}
                </p>
              </div>
            ))
          ) : (
            showEmpty('No acquisitions data')
          )}
        </div>
      )}

      {/* IP / Trademark Insights */}
      {(alwaysShowSections || ipqwerySummary?.ipqwery_num_patent_granted !== undefined || ipqwerySummary?.ipqwery_num_trademark_registered !== undefined) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🧠 IP & Trademarks</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {ipqwerySummary.ipqwery_num_patent_granted !== undefined ? (
              <div><span className="text-gray-500">Patents:</span> <span className="text-white">{ipqwerySummary.ipqwery_num_patent_granted}</span></div>
            ) : null}
            {ipqwerySummary.ipqwery_num_trademark_registered !== undefined ? (
              <div><span className="text-gray-500">Trademarks:</span> <span className="text-white">{ipqwerySummary.ipqwery_num_trademark_registered}</span></div>
            ) : null}
            {ipqwerySummary.ipqwery_popular_trademark_class ? (
              <div className="col-span-2"><span className="text-gray-500">Top class:</span> <span className="text-white">{esc(ipqwerySummary.ipqwery_popular_trademark_class)}</span></div>
            ) : null}
          </div>
          {ipqwerySummary?.ipqwery_num_patent_granted === undefined &&
          ipqwerySummary?.ipqwery_num_trademark_registered === undefined &&
          !ipqwerySummary?.ipqwery_popular_trademark_class
            ? showEmpty('No IP or trademark data')
            : null}
        </div>
      )}

      {/* Bombora */}
      {(alwaysShowSections || bomboraSummary?.identifier || bomboraSurgeList.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🔥 Bombora Topics</h4>
          {bomboraSurgeList.length > 0 ? (
            bomboraSurgeList.slice(0, 6).map((b: any, idx: number) => (
              <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                <p className="text-sm text-white">{esc(b.topic_identifier?.value || 'Topic')}</p>
                <p className="text-xs text-gray-400">Score: {b.surge_score} • WoW: {b.surge_wow_growth}</p>
              </div>
            ))
          ) : (
            showEmpty('No Bombora topics available')
          )}
        </div>
      )}

      {/* Founders */}
      {(alwaysShowSections || founders.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🚀 Founders</h4>
          {founders.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {founders.map((f: any, idx: number) => (
                <span key={idx} className="px-3 py-1 bg-purple-900/30 text-purple-300 text-sm rounded">{esc(f.value || f.name || String(f))}</span>
              ))}
            </div>
          ) : (
            showEmpty('No founders data')
          )}
        </div>
      )}

      {/* Awards */}
      {(alwaysShowSections || awards.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🏆 Awards</h4>
          {awards.length > 0 ? (
            awards.slice(0, 5).map((a: any, idx: number) => (
              <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                <p className="text-sm text-white">{esc(a.identifier?.value || a.title || 'Award')}</p>
                {a.key_event_date && <p className="text-xs text-gray-500">{a.key_event_date}</p>}
              </div>
            ))
          ) : (
            showEmpty('No awards data')
          )}
        </div>
      )}

      {/* Layoffs */}
      {(alwaysShowSections || layoffs.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🧑‍🤝‍🧑 Layoffs</h4>
          {layoffs.length > 0 ? (
            layoffs.slice(0, 5).map((l: any, idx: number) => (
              <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                <p className="text-sm text-gray-300">{esc(l.press_references?.[0]?.value || l.description || 'Layoff event')}</p>
                {l.key_event_date && <p className="text-xs text-gray-500">{l.key_event_date}</p>}
              </div>
            ))
          ) : (
            showEmpty('No layoffs data')
          )}
        </div>
      )}

      {/* Similar Companies / Hubs / Recommendations */}
      {(alwaysShowSections || recommendedSearch.length > 0 || orgSimilarityList.length > 0 || orgSimilarityOrgList.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🧭 Similar Companies & Hubs</h4>
          {recommendedSearch.length > 0 ? (
            recommendedSearch.slice(0, 3).map((r: any, idx: number) => (
              <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                <p className="text-sm text-white">{esc(r.identifier?.value || 'Hub')}</p>
                {Array.isArray(r.org_identifiers) && r.org_identifiers.length > 0 && (
                  <p className="text-xs text-gray-400">
                    Examples: {r.org_identifiers.slice(0, 5).map((o: any) => o.value).filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
            ))
          ) : (
            showEmpty('No similar companies or hubs available')
          )}
        </div>
      )}

      {/* Leadership */}
      {(alwaysShowSections || leadership.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">👔 Leadership</h4>
          {leadership.length > 0 ? (
            leadership.slice(0, 8).map((l: any, idx: number) => {
              const personName = l.person_identifier?.value || l.name || '';
              const title = l.title || l.role || '';
              const linkedinUrl = personName ? `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(personName)}` : '';
              return (
                <div key={idx} className="border-b border-gray-700 py-2 last:border-0 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-white font-semibold">{esc(personName)}</p>
                    <p className="text-xs text-gray-400">{esc(title)}</p>
                  </div>
                  {personName && <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline">Search LinkedIn</a>}
                </div>
              );
            })
          ) : (
            showEmpty('No leadership data')
          )}
        </div>
      )}

      {/* Contacts */}
      {(alwaysShowSections || contacts.num_contacts || contactFields.contact_email || contactFields.phone_number) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">📇 Contacts</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {contacts.num_contacts && <div><span className="text-gray-500">Contacts:</span> <span className="text-white">{contacts.num_contacts}</span></div>}
            {contacts.num_contact_emails && <div><span className="text-gray-500">Emails:</span> <span className="text-white">{contacts.num_contact_emails}</span></div>}
            {contacts.num_contact_phones && <div><span className="text-gray-500">Phones:</span> <span className="text-white">{contacts.num_contact_phones}</span></div>}
            {contactFields.contact_email && <div><span className="text-gray-500">Email:</span> <span className="text-white">{esc(contactFields.contact_email)}</span></div>}
            {contactFields.phone_number && <div><span className="text-gray-500">Phone:</span> <span className="text-white">{esc(contactFields.phone_number)}</span></div>}
          </div>
          {!contacts.num_contacts && !contactFields.contact_email && !contactFields.phone_number
            ? showEmpty('No contact data')
            : null}
        </div>
      )}

      {/* Advisor / Employee Summaries */}
      {(alwaysShowSections || advisorsSummary?.num_current_advisor_positions || currentEmployeesSummary?.num_current_positions || peopleHighlights?.num_current_positions) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">👥 Advisors & Employees</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {advisorsSummary.num_current_advisor_positions && (
              <div><span className="text-gray-500">Advisors:</span> <span className="text-white">{advisorsSummary.num_current_advisor_positions}</span></div>
            )}
            {currentEmployeesSummary.num_current_positions && (
              <div><span className="text-gray-500">Current roles:</span> <span className="text-white">{currentEmployeesSummary.num_current_positions}</span></div>
            )}
            {peopleHighlights.num_current_positions && (
              <div><span className="text-gray-500">Positions:</span> <span className="text-white">{peopleHighlights.num_current_positions}</span></div>
            )}
          </div>
          {!advisorsSummary?.num_current_advisor_positions &&
          !currentEmployeesSummary?.num_current_positions &&
          !peopleHighlights?.num_current_positions
            ? showEmpty('No advisor or employee highlights')
            : null}
        </div>
      )}

      {/* Product Launches */}
      {(alwaysShowSections || productLaunches.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🚀 Product Launches</h4>
          {productLaunches.length > 0 ? (
            productLaunches.slice(0, 5).map((p: any, idx: number) => (
              <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                <p className="text-sm text-gray-300">{esc(p.description || p.identifier?.value || 'Launch')}</p>
                {p.key_event_date && <p className="text-xs text-gray-500">{p.key_event_date}</p>}
              </div>
            ))
          ) : (
            showEmpty('No product launches data')
          )}
        </div>
      )}

      {/* SEMrush */}
      {(alwaysShowSections || semrushRankHeadline.semrush_visits_latest_month || semrushAttribution.semrush_last_updated) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">📈 SEMrush</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {semrushRankHeadline.semrush_visits_latest_month && (
              <div><span className="text-gray-500">Visits (latest):</span> <span className="text-white">{semrushRankHeadline.semrush_visits_latest_month}</span></div>
            )}
            {semrushRankHeadline.semrush_visits_mom_pct !== undefined && (
              <div><span className="text-gray-500">MoM %:</span> <span className="text-white">{(semrushRankHeadline.semrush_visits_mom_pct * 100).toFixed(1)}%</span></div>
            )}
            {semrushAttribution.semrush_last_updated && (
              <div className="col-span-2"><span className="text-gray-500">Last updated:</span> <span className="text-white">{esc(semrushAttribution.semrush_last_updated)}</span></div>
            )}
          </div>
          {(!semrushRankHeadline.semrush_visits_latest_month && !semrushAttribution.semrush_last_updated)
            ? showEmpty('No SEMrush data')
            : null}
        </div>
      )}

      {/* BuiltWith */}
      {(alwaysShowSections || builtwithSummary?.builtwith_num_technologies_used || builtwithTechList.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🧰 BuiltWith</h4>
          {builtwithSummary.builtwith_num_technologies_used ? (
            <p className="text-sm text-gray-300 mb-2">Technologies used: {builtwithSummary.builtwith_num_technologies_used}</p>
          ) : null}
          {builtwithTechList.length > 0 ? (
            builtwithTechList.slice(0, 6).map((t: any, idx: number) => (
              <span key={idx} className="inline-block mr-2 mb-2 px-2 py-1 bg-gray-700 text-xs text-gray-200 rounded">
                {esc(t.identifier?.value || t.value || 'Tech')}
              </span>
            ))
          ) : (
            showEmpty('No BuiltWith data')
          )}
        </div>
      )}

      {/* Siftery */}
      {(alwaysShowSections || sifterySummary?.siftery_num_products || sifteryProducts.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🧩 Siftery</h4>
          {sifterySummary.siftery_num_products ? (
            <p className="text-sm text-gray-300 mb-2">Products tracked: {sifterySummary.siftery_num_products}</p>
          ) : null}
          {sifteryProducts.length > 0 ? (
            sifteryProducts.slice(0, 6).map((p: any, idx: number) => (
              <div key={idx} className="text-xs text-gray-300 mb-1">
                {esc(p.product_identifier?.value || 'Product')} ({esc(p.status || 'unknown')})
              </div>
            ))
          ) : (
            showEmpty('No Siftery data')
          )}
        </div>
      )}

      {/* Apptopia */}
      {(alwaysShowSections || apptopiaSummary?.apptopia_total_downloads || apptopiaApps.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">📱 Apptopia</h4>
          <div className="grid grid-cols-2 gap-2 text-sm mb-2">
            {apptopiaSummary.apptopia_total_downloads && (
              <div><span className="text-gray-500">Total downloads:</span> <span className="text-white">{apptopiaSummary.apptopia_total_downloads}</span></div>
            )}
            {apptopiaHeadline.apptopia_total_downloads_mom_pct !== undefined && (
              <div><span className="text-gray-500">MoM %:</span> <span className="text-white">{(apptopiaHeadline.apptopia_total_downloads_mom_pct * 100).toFixed(1)}%</span></div>
            )}
          </div>
          {apptopiaApps.length > 0 ? (
            apptopiaApps.slice(0, 3).map((a: any, idx: number) => (
              <div key={idx} className="text-xs text-gray-300 mb-1">
                {esc(a.identifier?.value || 'App')} ({esc((a.stores || []).join(', '))})
              </div>
            ))
          ) : (
            showEmpty('No Apptopia data')
          )}
        </div>
      )}

      {/* Executive Changes */}
      {(alwaysShowSections || execChanges.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">📋 Recent Executive Changes</h4>
          {execChanges.length > 0 ? (
            execChanges.slice(0, 5).map((c: any, idx: number) => {
              const date = c.key_event_date || '';
              const desc = c.description || '';
              const newsUrl = c.press_references?.[0]?.source_url || '';
              return (
                <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                  <p className="text-sm text-gray-300">{esc(desc)}</p>
                  <div className="flex justify-between mt-1">
                    {date && <span className="text-xs text-gray-500">{date}</span>}
                    {newsUrl && <a href={esc(newsUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">Source →</a>}
                  </div>
                </div>
              );
            })
          ) : (
            showEmpty('No executive changes data')
          )}
        </div>
      )}

      {/* Products */}
      {(alwaysShowSections || products.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">📦 Products</h4>
          {products.length > 0 ? (
            products.slice(0, 6).map((p: any, idx: number) => (
              <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                <p className="text-sm text-white font-medium">{esc(p.name || p.identifier?.value || '')}</p>
                {p.description && <p className="text-xs text-gray-400 mt-1">{esc(p.description.substring(0, 150))}{p.description.length > 150 ? '...' : ''}</p>}
              </div>
            ))
          ) : (
            showEmpty('No products data')
          )}
        </div>
      )}

      {/* Partnerships */}
      {(alwaysShowSections || partnerships.length > 0) && (
        <div className="bg-gray-800 rounded-lg p-4">
          <h4 className="font-semibold text-purple-400 mb-3">🤝 Recent Partnerships</h4>
          {partnerships.length > 0 ? (
            partnerships.slice(0, 5).map((p: any, idx: number) => {
              const desc = p.description || '';
              const date = p.press_references?.[0]?.posted_on || '';
              const newsUrl = p.press_references?.[0]?.source_url || '';
              return (
                <div key={idx} className="border-b border-gray-700 py-2 last:border-0">
                  <p className="text-sm text-gray-300">{esc(desc)}</p>
                  <div className="flex justify-between mt-1">
                    {date && <span className="text-xs text-gray-500">{date}</span>}
                    {newsUrl && <a href={esc(newsUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">Source →</a>}
                  </div>
                </div>
              );
            })
          ) : (
            showEmpty('No partnerships data')
          )}
        </div>
      )}
    </>
  );
}

export default DataDrawer;
